import { getCart } from '@os/cart'
import { makeEventNamespace } from '@os/dom/events'
import { findOneElement } from '@os/dom/traversal'
import { openKlaviyoOnsiteForm } from '@os/klaviyo'
import { initMainBus, QUICKVIEW_LOAD } from '@os/messaging'

/**
 * `<product-form>` — the PDP and quickview product forms.
 *
 * Hand-authored module, included next to its markup with
 * `<script src="{{ 'product-form.js' | asset_url }}" type="module">`. Shared
 * behavior comes from the `@os/*` modules resolved by the theme import map. See
 * `docs/architecture.md`.
 *
 * Markup contract:
 * - `data-layout` selects the layout: `pdp` (default) or `quickview`. The
 *   layout also has to be in the `.js-data` JSON (`options.layout`), which is
 *   what the element reads.
 * - `.js-data` — the element configuration JSON (a child `<script>` tag).
 *
 * Size-aware colour options (PDP): a colour × size product can have colours
 * that are only stocked in one size — those variants do not exist, so their
 * swatch-image metafield is missing. The colour option is filtered to the
 * colours that have a variant in the currently picked size, so a colour that is
 * not stocked is removed (and re-added when a size that stocks it is picked).
 * When the picked colour is removed by a size change, the first colour still
 * stocked in that size becomes selected. Changing colour never filters or
 * disables the size options.
 *
 * This was a React app (a context provider, two layouts and sixteen
 * components) and is now plain DOM. The state machine and the component tree
 * are ported as-is; the difference is that the DOM is built once and each
 * region patches itself instead of re-rendering the whole form. A full
 * re-render would reset the quickview gallery's scroll position, restart the
 * countdown, and drop focus on the option radios.
 *
 * The PDP fetches the product JSON from `?view=api` (`templates/product.api.liquid`)
 * unless the snippet hydrated it (`hydrate_data`). The quickview fetches it when
 * the drawer emits `quickview:load` on the main bus.
 *
 * The former zod schemas are replaced by the native validators below
 * (`parseProductJson`, `parseProductFormData`, `parseItemPayload`), and the
 * former SWR cache is replaced by a single `fetch` per handle. The former
 * `_js/project/logger` debug instrumentation is dropped; the direct
 * `console.error` / `console.warn` calls are kept.
 *
 * A few latent bugs in the React version are fixed here rather than ported:
 * - The add-to-cart button stayed in its loading state forever when the cart
 *   engine answered `busy` (the early return skipped the reset).
 * - The `PRODUCT_UPDATE_SELECTED_VARIANT` bus message was sent twice per
 *   variant change (once by the effect, once by the re-run it triggered); it
 *   is sent once now.
 * - `errorRenderTarget` was never attached to an element, so a failed submit
 *   only reached the console; it still does (see `handleSubmit`).
 *
 * The pure helpers are inlined here (the element is their only consumer) and
 * exported so they can be unit-tested through the bundle.
 */

const DATA_SELECTOR = '.js-data'
const MOBILE_QUERY = '(max-width: 768px)'
const MONEY_FORMAT = '${{amount}}'
const CORE_DRAWER_CLOSE = 'core:drawer:close'
const OPEN_CART_DRAWER = 'request:open-cart-drawer'
const PRODUCT_UPDATE_SELECTED_VARIANT = 'notification:selected-variant'
const FINDATION_SEARCH_COMPLETE = 'searchComplete'
const FINDATION_WIDGET_ID = 'findation-widget-button'
const FINDATION_MAX_ATTEMPTS = 80
const FINDATION_ATTEMPT_DELAY = 50
const KLAVIYO_COMPANY_ID = 'LS9AkE'
const KLAVIYO_API_REVISION = '2025-07-15'
const ERROR_SUFFIX = '[a8bed63b434340e982acf019d921f602]'
const SKIP_OPTION_NAMES = ['size', 'sizes']
const STAR_POSITIONS = [0, 16.6, 33.2, 49.8, 66.4]
const GALLERY_BULLET_CLASS = 'product_form_productGallery__bullet'
const GALLERY_SRCSET_WIDTHS = [320, 480]

/**
 * CSS-module class names carried over from the React components. Their CSS used
 * to be injected at runtime by the compiled bundle; it now lives in
 * `snippets/product-form-styles.liquid`, so the strings have to keep matching
 * the source path-derived prefix.
 */
const LOADING_CLASS = {
  root: 'project_components_loading_style_root',
  spinner: 'project_components_loading_style_spinner'
}

const REVIEW_STARS_CLASS = {
  root: 'project_components_review_stars_style_root',
  stars: 'project_components_review_stars_style_stars',
  starsInner: 'project_components_review_stars_style_starsInner',
  content: 'project_components_review_stars_style_content',
  average: 'project_components_review_stars_style_average',
  count: 'project_components_review_stars_style_count'
}

/**
 * Style-module modifiers that resolve to a real class. The React code looked
 * these up in its generated class map; every other `option--*` /
 * `option__values--*` key was absent from that map, so it resolved to
 * `undefined`. Only the size modifier exists.
 */
const STYLE_MODIFIERS = {
  'option__values--size': 'product_form_option__values--size'
}

/**
 * Icons are rendered by `snippets/product-form-icons.liquid` as inert
 * `<template data-icon="...">` blocks and cloned on demand, so the form draws
 * the same artwork as the rest of the theme instead of carrying its own copies.
 *
 * @type {Map<string, Element>}
 */
const iconTemplates = new Map()

/**
 * Collects the host's icon templates. Must run before the host's children are
 * replaced, since that is where they live.
 *
 * @param {HTMLElement} host
 */
const registerIcons = (host) => {
  for (const template of host.querySelectorAll('template[data-icon]')) {
    if (!(template instanceof HTMLTemplateElement)) {
      continue
    }

    const name = template.dataset.icon
    const svg = template.content.firstElementChild

    if (name && svg) {
      iconTemplates.set(name, svg)
    }
  }
}

/**
 * Clones a registered icon. Returns `null` when the theme did not render it, so
 * a missing template costs an icon rather than throwing.
 *
 * The clone is marked `aria-hidden` and its `<title>` dropped: every call site
 * is decorative (it sits beside a text label or inside a control that already
 * has an `aria-label`), and the theme snippets' `<title id="...">` would
 * otherwise emit duplicate ids when an icon is used twice on a page.
 *
 * @param {string} name
 * @returns {Element | null}
 */
const icon = (name) => {
  const template = iconTemplates.get(name)

  if (!template) {
    return null
  }

  const clone = template.cloneNode(true)

  for (const title of clone.querySelectorAll('title')) {
    title.remove()
  }

  clone.setAttribute('aria-hidden', 'true')

  return clone
}

/* -------------------------------------------------------------------------- */
/* utilities                                                                  */
/* -------------------------------------------------------------------------- */

export const isObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const isPresent = (value) => value !== undefined && value !== null

/**
 * Lodash-compatible `kebabCase`, without the unicode astral/emoji handling the
 * theme never feeds it. Only used to build ids and to look up the `size` style
 * modifier, so ASCII word splitting is enough.
 *
 * @param {string} value
 * @returns {string}
 */
export const kebabCase = (value) =>
  String(value)
    .replace(/['\u2019]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
    .replace(/([0-9])([a-zA-Z])/g, '$1 $2')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase())
    .join('-')

/**
 * Formats a price the way Shopify's `money` filter does, using the shop's money
 * format string (defaults to `${{amount}}`).
 *
 * @param {number} amount
 * @param {string} [moneyFormat]
 * @returns {string}
 */
export const formatMoney = (amount, moneyFormat = MONEY_FORMAT) => {
  const format = moneyFormat || MONEY_FORMAT
  const matches = /\{\{\s*(\w+)\s*\}\}/.exec(format)

  if (!matches) {
    throw new Error('Money template has no `{{amount}}` placeholder')
  }

  const formatName = matches[1]

  const group = (value = 0, decimals = 2, separator = ',', decimal = '.') => {
    if (Number.isNaN(value)) {
      return '0'
    }

    const parts = (value / 100).toFixed(decimals).split('.')
    const whole = parts[0] || ''

    return (
      whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`) +
      (parts[1] ? decimal + parts[1] : '')
    )
  }

  const formatted =
    formatName === 'amount'
      ? group(amount, 2)
      : formatName === 'amount_no_decimals'
        ? group(amount, 0)
        : formatName === 'amount_with_comma_separator'
          ? group(amount, 2, '.', ',')
          : formatName === 'amount_with_space_separator'
            ? group(amount, 2, ' ', ',')
            : formatName === 'amount_with_period_and_space_separator'
              ? group(amount, 2, ' ', '.')
              : formatName === 'amount_no_decimals_with_comma_separator'
                ? group(amount, 0, '.', ',')
                : formatName === 'amount_no_decimals_with_space_separator'
                  ? group(amount, 0, ' ')
                  : formatName === 'amount_with_apostrophe_separator'
                    ? group(amount, 2, "'", '.')
                    : amount.toString(10)

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, formatted)
}

const ISO_DATE_ONLY = /^(\d{4}-\d{2}-\d{2})$/
const ISO_LOCAL_DATETIME =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)(Z|[+-]\d{2}:?\d{2})?$/i

/**
 * Parses a countdown timestamp from the section settings. Accepts unix seconds,
 * unix milliseconds, `YYYY-MM-DD`, and ISO date-times with or without an offset.
 *
 * @param {string | null | undefined} timestamp
 * @returns {Date | null}
 */
export const parseCountdownTimestamp = (timestamp) => {
  if (!timestamp) {
    return null
  }

  const trimmed = timestamp.trim()
  if (!trimmed) {
    return null
  }

  if (/^\d+$/.test(trimmed)) {
    const numeric = Number(trimmed)
    const milliseconds = trimmed.length <= 10 ? numeric * 1000 : numeric
    const unixDate = new Date(milliseconds)
    return Number.isNaN(unixDate.getTime()) ? null : unixDate
  }

  const dateOnlyMatch = ISO_DATE_ONLY.exec(trimmed)
  let normalized = trimmed.replace(' ', 'T')

  if (dateOnlyMatch?.[1]) {
    normalized = `${dateOnlyMatch[1]}T00:00:00Z`
  } else {
    const match = ISO_LOCAL_DATETIME.exec(trimmed)
    const date = match?.[1]
    const time = match?.[2]

    if (date && time) {
      const offset = match?.[3]
      const timezone = offset ? offset.toUpperCase() : 'Z'
      normalized = `${date}T${time}${timezone}`
    }
  }

  const targetDate = new Date(normalized)
  return Number.isNaN(targetDate.getTime()) ? null : targetDate
}

/**
 * The store root path, so links and fetches keep working on locale subfolders.
 *
 * @returns {string}
 */
export const getShopifyRoot = () => {
  const root = window.Shopify?.routes?.root

  return typeof root === 'string' && root.length > 0 ? root : '/'
}

/**
 * @param {string} path
 * @returns {URL}
 */
export const atShopifyRoot = (path) => {
  const relative = path.startsWith('/') ? path.slice(1) : path

  return new URL([getShopifyRoot(), relative].join(''), window.location.origin)
}

/**
 * @param {{ path: string }} input
 * @returns {string}
 */
export const getLocaleUrl = ({ path }) => atShopifyRoot(path).toString()

/**
 * Reads a translated string from `window.themeLocales`, decoding the
 * percent-encoded value and substituting `{{ placeholder }}` tokens.
 *
 * @param {string} key
 * @param {Record<string, string>} [replacements]
 * @returns {string}
 */
export const getLocaleString = (key, replacements) => {
  try {
    const locale = window.themeLocales
    if (!locale || typeof locale !== 'object') {
      return ''
    }

    let value = locale[key]
    if (!value || typeof value !== 'string') {
      return ''
    }

    const textArea = document.createElement('textarea')
    textArea.innerHTML = decodeURIComponent(value)
    value = textArea.value

    if (replacements && typeof replacements === 'object') {
      for (const [placeholder, replacement] of Object.entries(replacements)) {
        const regex = new RegExp(`{{\\s*${placeholder}\\s*}}`, 'g')
        value = value.replace(regex, replacement)
      }
    }

    return value
  } catch (error) {
    console.error('Failed to retrieve or decode locale string', key, error)
    return ''
  }
}

/**
 * @param {string} queryParams
 * @returns {Record<string, string>}
 */
export const queryParamsToObject = (queryParams) =>
  Object.fromEntries(new URLSearchParams(queryParams))

/* -------------------------------------------------------------------------- */
/* DOM helpers                                                                */
/* -------------------------------------------------------------------------- */

const appendChildren = (node, children) => {
  for (const child of children.flat(Infinity)) {
    if (child === null || child === undefined || child === false) {
      continue
    }

    node.append(
      child instanceof Node ? child : document.createTextNode(String(child))
    )
  }
}

/**
 * Replaces a node's children, dropping `null` / `undefined` / `false` entries.
 * `Node.replaceChildren` stringifies those, which renders a literal "null".
 *
 * @param {HTMLElement} node
 * @param {unknown[]} children
 */
const setChildren = (node, children) => {
  node.replaceChildren(
    ...children.filter(
      (child) => child !== null && child !== undefined && child !== false
    )
  )
}

/**
 * Creates an element.
 *
 * `props` keys are attributes, except:
 * - `text` sets `textContent`
 * - `html` sets `innerHTML` (trusted markup only: icons and Liquid-rendered
 *   price HTML)
 * - `class` sets `className`
 * - `style` assigns onto `node.style` (camelCase CSS properties)
 * - `on` maps event types to listeners
 *
 * `false`, `null` and `undefined` values are skipped, so boolean attributes can
 * be passed directly.
 *
 * @param {string} tag
 * @param {Record<string, unknown>} [props]
 * @param {unknown[]} [children]
 * @returns {HTMLElement}
 */
export const el = (tag, props = {}, children = []) => {
  const node = document.createElement(tag)

  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === false) {
      continue
    }

    if (key === 'text') {
      node.textContent = String(value)
      continue
    }

    if (key === 'html') {
      node.innerHTML = String(value)
      continue
    }

    if (key === 'class') {
      node.className = String(value)
      continue
    }

    if (key === 'style') {
      Object.assign(node.style, value)
      continue
    }

    if (key === 'on') {
      for (const [type, handler] of Object.entries(value)) {
        node.addEventListener(type, handler)
      }
      continue
    }

    node.setAttribute(key, value === true ? '' : String(value))
  }

  appendChildren(node, children)

  return node
}

/**
 * Replaces a region's children while keeping focus on the element the user was
 * on, when it carries a stable `id` or `data-focus-key`. Rebuilding a region
 * otherwise drops focus to `<body>`, which breaks keyboard use of the option
 * radios.
 *
 * @param {HTMLElement} container
 * @param {() => void} update
 */
const preserveFocus = (container, update) => {
  const active = document.activeElement
  const focusKey =
    active instanceof HTMLElement && container.contains(active)
      ? active.id || active.getAttribute('data-focus-key') || ''
      : ''
  const activeId = active instanceof HTMLElement ? active.id : ''

  update()

  if (!focusKey) {
    return
  }

  const restored = activeId
    ? container.querySelector(`[id="${focusKey}"]`)
    : container.querySelector(`[data-focus-key="${focusKey}"]`)

  if (restored instanceof HTMLElement) {
    restored.focus()
  }
}

/* -------------------------------------------------------------------------- */
/* pure product helpers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * @param {number | null} sellingPlanId
 * @param {object | null} selectedVariant
 * @returns {object | null}
 */
export const getSelectedSellingPlanAllocation = (
  sellingPlanId,
  selectedVariant
) => {
  if (!sellingPlanId || !selectedVariant) {
    return null
  }

  const allocations = selectedVariant.metafields?.selling_plan_allocations ?? []

  return (
    allocations.find(
      (allocation) => allocation.selling_plan_id === sellingPlanId
    ) ?? null
  )
}

/**
 * @param {object | null} data
 * @returns {object | null}
 */
export const getInitialVariant = (data) => {
  if (!data) {
    return null
  }

  const firstShopifyVariant =
    data.metafields?.selected_or_first_available_variant ??
    data.product?.variants?.find((variant) => variant.available === true)
  const firstShopifyVariantId = firstShopifyVariant?.id ?? null

  if (!firstShopifyVariantId) {
    return null
  }

  return (
    data.variants?.find(
      (variant) => variant.variant.id === firstShopifyVariantId
    ) ?? null
  )
}

/**
 * @param {string[] | null} options
 * @param {object | null} data
 * @returns {object | null}
 */
export const getSelectedVariantFromOptions = (options, data) => {
  if (!options || !data) {
    return null
  }

  return (
    data.variants?.find((variant) =>
      variant.variant.options.every((option) => options.includes(option))
    ) ?? null
  )
}

const isImageUrl = (url) => typeof url === 'string' && url.length > 0

/**
 * The images the gallery should show for a selection: the variant's
 * `variant_images` metafield, or the option-value swatch images as a fallback.
 *
 * @param {object | null} selectedProduct
 * @param {number | null | undefined} selectedVariantId
 * @returns {string[]}
 */
export const getVariantImageUrls = (selectedProduct, selectedVariantId) => {
  if (!selectedProduct || selectedVariantId == null) {
    return []
  }

  const fromSelectedVariant = selectedProduct.variants?.find(
    (item) => item.variant.id === selectedVariantId
  )?.metafields?.variant_images

  const selectedVariantUrls = Array.from(
    new Set((fromSelectedVariant ?? []).filter(isImageUrl))
  )

  if (selectedVariantUrls.length > 0) {
    return selectedVariantUrls
  }

  return Array.from(
    new Set(
      (selectedProduct.metafields?.options_with_values ?? [])
        .flatMap((option) => option.values ?? [])
        .filter((value) => value?.variant?.id === selectedVariantId)
        .flatMap((value) => value?.swatch?.variant_images ?? [])
        .filter(isImageUrl)
    )
  )
}

const isImageMedia = (item) =>
  Boolean(item && typeof item === 'object' && 'src' in item)

/**
 * Resolves the images the quickview gallery should render for the current
 * selection. Variant images win (first image first, so changing variants swaps
 * the gallery); when the variant has none, the product media is used.
 *
 * @param {object[] | null} media
 * @param {object | null} selectedProduct
 * @param {number | null | undefined} selectedVariantId
 * @param {string | null} [fallbackAlt]
 * @returns {{ src: string, srcset?: string | null, alt?: string | null, width?: number | null, height?: number | null }[]}
 */
export const getGalleryImages = (
  media,
  selectedProduct,
  selectedVariantId,
  fallbackAlt = null
) => {
  const variantImageUrls = getVariantImageUrls(
    selectedProduct,
    selectedVariantId
  )

  if (variantImageUrls.length > 0) {
    return variantImageUrls.map((src) => ({ src, alt: fallbackAlt }))
  }

  return (media ?? []).filter(isImageMedia).flatMap((item) => {
    if (!isImageUrl(item.src)) {
      return []
    }

    return [
      {
        src: item.src,
        srcset: item.srcset ?? null,
        alt: item.alt ?? fallbackAlt,
        width: item.width ?? null,
        height: item.height ?? null
      }
    ]
  })
}

const normalizeWaitlistFormId = (formId) => formId?.trim() ?? ''

/**
 * Variant-level waitlist config wins when any variant defines one, so a product
 * can mix waitlisted and non-waitlisted variants.
 *
 * @param {object | null} selectedProduct
 * @param {object | null} selectedVariant
 * @returns {string}
 */
export const getWaitlistFormId = (selectedProduct, selectedVariant) => {
  const selectedVariantWaitlistFormId = normalizeWaitlistFormId(
    selectedVariant?.metafields?.klaviyo_waitlist_form_id
  )

  const hasVariantLevelWaitlistConfig = Boolean(
    selectedProduct?.variants?.some((variant) =>
      Boolean(
        normalizeWaitlistFormId(variant.metafields?.klaviyo_waitlist_form_id)
      )
    )
  )

  if (hasVariantLevelWaitlistConfig) {
    return selectedVariantWaitlistFormId
  }

  return (
    selectedVariantWaitlistFormId ||
    normalizeWaitlistFormId(
      selectedProduct?.metafields?.klaviyo_waitlist_form_id
    )
  )
}

/**
 * @param {{ sellingPlanAllocation: object, format?: string }} input
 * @returns {string}
 */
export const getSellingPlanAllocationSavingsPrice = ({
  sellingPlanAllocation,
  format = MONEY_FORMAT
}) => {
  const { price, compare_at_price: compareAtPrice } = sellingPlanAllocation
  const difference = compareAtPrice && price ? compareAtPrice - price : 0

  return difference > 0 ? formatMoney(difference, format) : ''
}

/**
 * @param {{ sellingPlanAllocation: object }} input
 * @returns {number}
 */
export const getSellingPlanAllocationSavingsPercentage = ({
  sellingPlanAllocation
}) => {
  const { price, compare_at_price: compareAtPrice } = sellingPlanAllocation

  if (!compareAtPrice || !price || compareAtPrice <= price) {
    return 0
  }

  return Math.round(((compareAtPrice - price) / compareAtPrice) * 100)
}

/**
 * @param {{ quantity: number, min?: number | null, max?: number | null }} input
 * @returns {number}
 */
export const constrainQuantity = ({ quantity, min, max }) => {
  const minQuantity = min ?? 1
  let constrainedQuantity = Math.max(minQuantity, quantity)

  if (max !== null && max !== undefined) {
    constrainedQuantity = Math.min(constrainedQuantity, max)
  }

  return constrainedQuantity
}

/**
 * @param {{ selling_plan_id?: number | null, selling_plan_highlighted?: boolean | null }[] | null} plans
 * @returns {{ selling_plan_id: number } | null}
 */
export const findHighlightedSellingPlan = (plans) => {
  if (!plans) {
    return null
  }

  const highlightedPlan = plans.find(
    (planData) => planData.selling_plan_highlighted === true
  )

  return highlightedPlan?.selling_plan_id
    ? { selling_plan_id: highlightedPlan.selling_plan_id }
    : null
}

/* -------------------------------------------------------------------------- */
/* price                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Rewrites the money in a Liquid-rendered price fragment to match the current
 * quantity, using the `data-*` hooks the price snippets emit.
 *
 * @param {string | null} html
 * @param {number | null} price
 * @param {number | null} compareAtPrice
 * @param {number} quantity
 * @returns {string | null}
 */
export const replacePriceValuesInHtml = (
  html,
  price,
  compareAtPrice,
  quantity
) => {
  if (!html || price === null) {
    return html
  }

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  const subscriptionPriceElements = tempDiv.querySelectorAll(
    '[data-subscription-price]'
  )
  const subscriptionCompareAtPriceElements = tempDiv.querySelectorAll(
    '[data-subscription-compare-at-price]'
  )
  const variantPriceElements = tempDiv.querySelectorAll('[data-variant-price]')
  const compareAtPriceElements = tempDiv.querySelectorAll(
    '[data-compare-at-price]'
  )
  const youSaveElements = tempDiv.querySelectorAll('[data-you-save]')

  if (
    subscriptionPriceElements.length === 0 &&
    subscriptionCompareAtPriceElements.length === 0 &&
    variantPriceElements.length === 0 &&
    compareAtPriceElements.length === 0 &&
    youSaveElements.length === 0
  ) {
    return html
  }

  const formattedPrice = formatMoney(price * quantity).replace('.00', '')

  for (const element of subscriptionPriceElements) {
    element.textContent = formattedPrice
  }

  for (const element of variantPriceElements) {
    if (element.getAttribute('data-is-free-gift') === 'true') {
      continue
    }

    const priceAmount = element.getAttribute('data-variant-price-amount')
    const parsedPriceAmount =
      priceAmount !== null ? Number.parseFloat(priceAmount) : null
    const amount = Number.isFinite(parsedPriceAmount)
      ? parsedPriceAmount
      : price

    if (amount === null) {
      continue
    }

    element.textContent = formatMoney(amount * quantity).replace('.00', '')
  }

  if (compareAtPrice !== null) {
    const formattedCompareAtPrice = formatMoney(
      compareAtPrice * quantity
    ).replace('.00', '')

    for (const element of subscriptionCompareAtPriceElements) {
      element.textContent = formattedCompareAtPrice
    }

    for (const element of compareAtPriceElements) {
      const compareAmount = element.getAttribute('data-compare-at-price-amount')
      const parsedCompareAmount =
        compareAmount !== null ? Number.parseFloat(compareAmount) : null
      const amount = Number.isFinite(parsedCompareAmount)
        ? parsedCompareAmount
        : compareAtPrice

      if (amount === null) {
        continue
      }

      element.textContent = formatMoney(amount * quantity).replace('.00', '')
    }
  }

  for (const element of youSaveElements) {
    const savingsAmount = element.getAttribute('data-you-save-amount')
    const parsedSavingsAmount =
      savingsAmount !== null ? Number.parseFloat(savingsAmount) : null

    if (parsedSavingsAmount === null || !Number.isFinite(parsedSavingsAmount)) {
      continue
    }

    element.textContent = getLocaleString('products.product.save_amount', {
      amount: formatMoney(parsedSavingsAmount * quantity)
    })
  }

  return tempDiv.innerHTML
}

const getBaseSelectedPrice = ({
  selectedVariant,
  purchaseType,
  selectedSellingPlanAllocation
}) => {
  let price = null
  let compareAtPrice = null
  let html = null
  let youSaveHtml = null

  if (purchaseType === 'subscription' && selectedVariant?.variant.available) {
    price =
      selectedSellingPlanAllocation?.price_adjustments?.[0]?.price ??
      selectedVariant?.variant.price ??
      null

    compareAtPrice =
      selectedSellingPlanAllocation?.price_adjustments?.[0]?.compare_at_price ??
      selectedSellingPlanAllocation?.compare_at_price ??
      selectedVariant?.variant.compare_at_price ??
      null

    html =
      selectedSellingPlanAllocation?.subscription_price_html ??
      selectedVariant?.metafields?.product_price_html ??
      null

    youSaveHtml =
      selectedSellingPlanAllocation?.you_save_html_v2 ??
      selectedSellingPlanAllocation?.you_save_html ??
      selectedVariant?.metafields?.you_save_html_v2 ??
      selectedVariant?.metafields?.you_save_html ??
      null
  } else {
    price = selectedVariant?.variant.price ?? null
    compareAtPrice = selectedVariant?.variant.compare_at_price ?? null
    html = selectedVariant?.metafields?.product_price_html ?? null
    youSaveHtml =
      selectedVariant?.metafields?.you_save_html_v2 ??
      selectedVariant?.metafields?.you_save_html ??
      null
  }

  return {
    price,
    compare_at_price: compareAtPrice,
    price_html: html,
    you_save_html: youSaveHtml
  }
}

/**
 * The selected price for the current variant, purchase type and quantity.
 *
 * @param {{ selectedVariant: object | null, purchaseType: string, selectedSellingPlanAllocation: object | null, quantity: number }} input
 * @returns {{
 *   price: number | null,
 *   compare_at_price: number | null,
 *   price_html: string | null,
 *   you_save_html: string | null,
 *   price_with_quantity: number | null,
 *   price_html_with_quantity: string | null,
 *   you_save_html_with_quantity: string | null
 * }}
 */
export const getSelectedPrice = ({
  selectedVariant,
  purchaseType,
  selectedSellingPlanAllocation,
  quantity
}) => {
  const {
    price,
    compare_at_price: compareAtPrice,
    price_html: html,
    you_save_html: youSaveHtml
  } = getBaseSelectedPrice({
    selectedVariant,
    purchaseType,
    selectedSellingPlanAllocation
  })

  return {
    price,
    compare_at_price: compareAtPrice,
    price_html: html,
    you_save_html: youSaveHtml,
    price_with_quantity: price !== null ? price * quantity : null,
    price_html_with_quantity: replacePriceValuesInHtml(
      html,
      price,
      compareAtPrice,
      quantity
    ),
    you_save_html_with_quantity: replacePriceValuesInHtml(
      youSaveHtml,
      price,
      compareAtPrice,
      quantity
    )
  }
}

const availabilityState = (key, disabled, localeKey, backorder) => ({
  key,
  disabled,
  text: getLocaleString(localeKey),
  backorder
})

/**
 * The add-to-cart button's availability state. Replaces the React
 * `useProductAvailability` hook (it was never a hook, only a lookup).
 *
 * @param {{ product: object | null, variant: object | null }} input
 * @returns {{ key: string, disabled: boolean, text: string, backorder: boolean }}
 */
export const getProductAvailability = ({ product, variant }) => {
  const unknown = availabilityState(
    'unknown',
    true,
    'products.product.unavailable',
    false
  )

  if (!product || !variant) {
    return unknown
  }

  if (variant.variant.available) {
    return availabilityState(
      'available',
      false,
      'products.product.available',
      false
    )
  }

  return availabilityState('sold_out', true, 'products.product.sold_out', false)
}

/* -------------------------------------------------------------------------- */
/* data parsing                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Native replacement for the former zod `JSONProductSchema.safeParse`. The
 * schema validated and stripped the `?view=api` payload; the renderers only read
 * declared fields, so a shape check is enough — the raw objects are a superset
 * of what the schema kept.
 *
 * @param {unknown} raw
 * @returns {object | null}
 */
export const parseProductJson = (raw) => {
  if (!isObject(raw)) {
    return null
  }

  if (!isObject(raw.product) || !isObject(raw.metafields)) {
    return null
  }

  if (!Array.isArray(raw.variants)) {
    return null
  }

  return {
    product: raw.product,
    variants: raw.variants,
    metafields: raw.metafields
  }
}

/**
 * Native replacement for the former zod `ProductFormDataSchema.safeParse`.
 * `ssr_html` is the only required field; the rest are read defensively.
 *
 * @param {unknown} raw
 * @returns {object | null}
 */
export const parseProductFormData = (raw) => {
  if (!isObject(raw) || typeof raw.ssr_html !== 'string') {
    return null
  }

  return raw
}

const parseHydratedProduct = (hydrateData) => {
  if (!hydrateData) {
    return null
  }

  try {
    return parseProductJson(JSON.parse(hydrateData))
  } catch {
    return null
  }
}

/**
 * Flattens a form's entries into name/value pairs, dropping file values.
 *
 * @param {HTMLFormElement} form
 * @returns {{ name: string, value: string }[]}
 */
const readFormDataPairs = (form) =>
  Array.from(new FormData(form).entries()).reduce((pairs, [key, value]) => {
    if (typeof value !== 'string') {
      return pairs
    }

    return [...pairs, { name: key, value }]
  }, [])

const PROPERTY_KEY_RE = /^properties\[(.*)\]$/i

/**
 * Folds `properties[Foo]` form fields into a `properties` object, as Shopify's
 * `/cart/add` endpoint expects.
 *
 * @param {{ name: string, value: string }[]} pairs
 * @returns {{ properties: Record<string, unknown>, [key: string]: unknown }}
 */
export const createRawItemPayload = (pairs) =>
  pairs.reduce(
    (acc, { name, value }) => {
      if (name === 'properties') {
        return acc
      }

      if (name.startsWith('properties')) {
        if (!PROPERTY_KEY_RE.test(name)) {
          return acc
        }

        const propertyKey = name.trim().replace(PROPERTY_KEY_RE, '$1').trim()

        if (!propertyKey) {
          return acc
        }

        acc.properties[propertyKey] = value
        return acc
      }

      acc[name] = value
      return acc
    },
    { properties: {} }
  )

const toInteger = (value) => {
  const parsed =
    typeof value === 'string' ? Number.parseFloat(value) : Number(value)

  if (typeof parsed !== 'number' || Number.isNaN(parsed)) {
    return null
  }

  return Number.isInteger(parsed) ? parsed : null
}

/**
 * Native replacement for the former zod `SingleItemPayload.safeParse`. Form
 * values arrive as strings, so `id`, `quantity` and `selling_plan` are coerced
 * and must land on positive integers; unknown keys are dropped, matching the
 * schema's strip behavior.
 *
 * @param {unknown} raw
 * @returns {{ id: number, quantity: number, properties?: object, selling_plan?: number | null } | null}
 */
export const parseItemPayload = (raw) => {
  if (!isObject(raw)) {
    return null
  }

  const id = toInteger(raw.id)
  const quantity = toInteger(raw.quantity)

  if (id === null || id < 1 || quantity === null || quantity < 1) {
    return null
  }

  const payload = { id, quantity }

  if (raw.properties !== undefined) {
    if (!isObject(raw.properties)) {
      return null
    }

    payload.properties = raw.properties
  }

  if ('selling_plan' in raw) {
    if (raw.selling_plan === null) {
      payload.selling_plan = null
    } else {
      const sellingPlan = toInteger(raw.selling_plan)

      if (sellingPlan === null || sellingPlan < 1) {
        return null
      }

      payload.selling_plan = sellingPlan
    }
  }

  return payload
}

/* -------------------------------------------------------------------------- */
/* widgets                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The loading placeholder: the server-rendered skeleton when the snippet
 * captured one, otherwise a spinner.
 *
 * @param {string} ssrHtml
 * @returns {HTMLElement}
 */
const createLoading = (ssrHtml) => {
  if (ssrHtml) {
    return el('div', { html: ssrHtml })
  }

  return el('div', { class: LOADING_CLASS.root }, [
    el('div', { class: 'product_form_loading' }, [
      (() => {
        const spinner = icon('spinner')
        if (spinner instanceof Element) {
          spinner.setAttribute('class', 'product_form_loading__spinner')
        }
        return spinner
      })()
    ])
  ])
}

const MIN_QUANTITY = 1
const MAX_QUANTITY = undefined

/**
 * The quantity stepper. Its value is patched rather than rebuilt so typing in
 * the input never loses focus.
 *
 * @param {{ getQuantity: () => number, setQuantity: (quantity: number) => void }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createQuantity = ({ getQuantity, setQuantity }) => {
  const input = el('input', {
    id: 'quantity-input',
    class: 'product_form_quantity__input',
    type: 'number',
    min: MIN_QUANTITY,
    'aria-label': getLocaleString('products.product.quantity')
  })

  const decrease = el(
    'button',
    {
      type: 'button',
      class: 'product_form_quantity__button',
      'aria-label': getLocaleString('products.product.decrease_quantity'),
      on: {
        click: () => {
          setQuantity(Math.max(MIN_QUANTITY, getQuantity() - 1))
        }
      }
    },
    [icon('minus')]
  )

  const increase = el(
    'button',
    {
      type: 'button',
      class: 'product_form_quantity__button',
      'aria-label': getLocaleString('products.product.increase_quantity'),
      on: {
        click: () => {
          const next = getQuantity() + 1
          setQuantity(MAX_QUANTITY ? Math.min(next, MAX_QUANTITY) : next)
        }
      }
    },
    [icon('plus')]
  )

  input.addEventListener('input', () => {
    const value = Number.parseInt(input.value, 10)

    if (Number.isNaN(value)) {
      return
    }

    setQuantity(constrainQuantity({ quantity: value }))
  })

  const el_ = el('div', { class: 'product_form_quantity' }, [
    decrease,
    input,
    increase
  ])

  const sync = () => {
    const quantity = getQuantity()

    if (input.value !== String(quantity)) {
      input.value = String(quantity)
    }

    decrease.disabled = quantity <= MIN_QUANTITY
    increase.disabled = MAX_QUANTITY !== undefined && quantity >= MAX_QUANTITY
  }

  sync()

  return { el: el_, sync }
}

/**
 * The add-to-cart button, including its loading state and price. Patched in
 * place so the button keeps focus across state changes.
 *
 * @param {{ formId: string, getState: () => object, getPriceHtml: () => string | null }} input
 * @returns {{ el: HTMLElement | null, sync: () => void }}
 */
const createAddToCartButton = ({ formId, getState, getPriceHtml }) => {
  const state = getState()

  if (!state.selectedProduct || !state.selectedVariant) {
    return { el: null, sync: () => undefined }
  }

  const loading = el('span', {
    class: 'product_form_addToCartButton__loading',
    'aria-hidden': 'true'
  })

  const label = el('span', { class: 'product_form_addToCartButton__label' })
  const price = el('span', { class: 'product_form_addToCartButton__price' })
  const content = el(
    'span',
    { class: 'product_form_addToCartButton__content' },
    [label, price]
  )

  const button = el(
    'button',
    {
      type: 'submit',
      form: formId ?? undefined,
      class: 'product_form_addToCartButton',
      'data-focus-key': 'add-to-cart'
    },
    [content]
  )

  const sync = () => {
    const current = getState()

    if (!current.selectedProduct || !current.selectedVariant) {
      return
    }

    const { disabled, text } = getProductAvailability({
      product: current.selectedProduct,
      variant: current.selectedVariant
    })
    const isAddingToCart = Boolean(current.isAddingToCart)
    const isDisabled = disabled && !isAddingToCart

    button.disabled = isDisabled
    button.setAttribute('aria-disabled', String(isDisabled))
    button.setAttribute('aria-busy', String(isAddingToCart))
    button.setAttribute(
      'aria-label',
      getLocaleString('products.product.add_to_cart', {
        title: current.selectedVariant.variant.title
      })
    )

    if (isAddingToCart) {
      button.setAttribute('aria-live', 'polite')
    } else {
      button.removeAttribute('aria-live')
    }

    button.className = isAddingToCart
      ? 'is-adding-to-cart product_form_addToCartButton'
      : 'product_form_addToCartButton'

    const spinner = icon('spinner')

    if (isAddingToCart) {
      loading.replaceChildren(
        el('div', { class: LOADING_CLASS.root }, [spinner])
      )
      if (!button.contains(loading)) {
        button.prepend(loading)
      }
    } else {
      loading.remove()
    }

    content.className = isAddingToCart
      ? 'product_form_addToCartButton__content product_form_addToCartButton__content--loading'
      : 'product_form_addToCartButton__content'

    label.textContent = text
    price.innerHTML = getPriceHtml() ?? ''
  }

  sync()

  return { el: button, sync }
}

/**
 * Tracks the `max-width: 768px` breakpoint the waitlist button labels switch on.
 *
 * @param {object} namespace
 * @param {(isMobile: boolean) => void} onChange
 * @returns {() => boolean}
 */
const trackMobileQuery = (namespace, onChange) => {
  if (typeof window.matchMedia !== 'function') {
    return () => false
  }

  const query = window.matchMedia(MOBILE_QUERY)
  let isMobile = query.matches

  const handleChange = (event) => {
    isMobile = event.matches
    onChange(isMobile)
  }

  query.addEventListener('change', handleChange)
  namespace.addCleanup(() => query.removeEventListener('change', handleChange))

  return () => isMobile
}

/**
 * The Klaviyo waitlist button.
 *
 * @param {{ getState: () => object, getIsMobile: () => boolean }} input
 * @returns {{ el: HTMLElement | null, sync: () => void }}
 */
const createWaitlist = ({ getState, getIsMobile }) => {
  const state = getState()
  const formId = getWaitlistFormId(state.selectedProduct, state.selectedVariant)
  const enableKlaviyoWaitlist =
    state.selectedProduct?.metafields?.enable_klaviyo_waitlist ?? false

  if (!enableKlaviyoWaitlist || !formId) {
    return { el: null, sync: () => undefined }
  }

  const label = el('span')

  const el_ = el('div', { class: 'product_form_waitlist' }, [
    el(
      'button',
      {
        type: 'button',
        class: 'product_form_waitlist__button',
        on: {
          click: () => {
            if (formId) {
              openKlaviyoOnsiteForm(formId)
            }
          }
        }
      },
      [label]
    )
  ])

  const sync = () => {
    const current = getState()
    const metafields = current.selectedProduct?.metafields
    const fallback = getLocaleString('products.waitlist.submit_label')
    const desktop = metafields?.waitlist_button_label_desktop ?? fallback
    const mobile = metafields?.waitlist_button_label_mobile ?? fallback

    label.textContent = getIsMobile() ? mobile : desktop
  }

  sync()

  return { el: el_, sync }
}

const KLAVIYO_BACK_IN_STOCK_ENDPOINT = (companyId) =>
  `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`

/**
 * The back-in-stock email form. Its local state (`email`, `status`, `error`)
 * lives on the caller so it survives a region rebuild, matching the way React
 * kept the component instance mounted.
 *
 * @param {{
 *   klaviyoSettings: object | null | undefined,
 *   getVariantId: () => string,
 *   local: { email: string, status: string, error: string | null }
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createBackInStock = ({ klaviyoSettings, getVariantId, local }) => {
  const companyId = klaviyoSettings?.company_id || KLAVIYO_COMPANY_ID

  const emailInput = el('input', {
    type: 'email',
    class: 'product_form_backInStock__input',
    placeholder: getLocaleString('products.back_in_stock.email_placeholder'),
    'aria-label': getLocaleString('products.back_in_stock.email_placeholder')
  })

  const errorMessage = el('div', {
    id: 'back-in-stock-error',
    class: 'product_form_backInStock__error',
    role: 'alert'
  })

  const buttonLabel = el('span')

  const button = el(
    'button',
    {
      type: 'button',
      class: 'product_form_backInStock__button',
      on: { click: () => void subscribe() }
    },
    [buttonLabel]
  )

  const form = el('div', { class: 'product_form_backInStock__form' }, [
    el('div', { class: 'product_form_backInStock__inputWrapper' }, [
      emailInput
    ]),
    errorMessage,
    button
  ])

  const success = el(
    'div',
    { class: 'product_form_backInStock__success', role: 'status' },
    [
      el('p', {
        text: getLocaleString('products.back_in_stock.back_in_stock_success')
      })
    ]
  )

  const el_ = el('div', { class: 'product_form_backInStock' }, [form])

  const render = () => {
    const isLoading = local.status === 'loading'
    const hasError = local.status === 'error'

    if (local.status === 'success') {
      el_.replaceChildren(success)
      return
    }

    el_.replaceChildren(form)

    if (emailInput.value !== local.email) {
      emailInput.value = local.email
    }

    emailInput.disabled = isLoading
    emailInput.setAttribute('aria-invalid', String(hasError))

    if (hasError) {
      emailInput.setAttribute('aria-describedby', 'back-in-stock-error')
    } else {
      emailInput.removeAttribute('aria-describedby')
    }

    errorMessage.textContent = hasError ? (local.error ?? '') : ''
    errorMessage.hidden = !(hasError && local.error)

    button.disabled = isLoading
    button.setAttribute('aria-busy', String(isLoading))
    buttonLabel.textContent = isLoading
      ? getLocaleString('products.back_in_stock.loading')
      : getLocaleString('products.back_in_stock.notify_me')
  }

  emailInput.addEventListener('input', () => {
    local.email = emailInput.value

    if (local.status === 'error' || local.status === 'success') {
      local.status = 'idle'
      local.error = null
      render()
    }
  })

  const subscribe = async () => {
    const variantId = getVariantId()

    if (!local.email || !variantId || !companyId) {
      local.error = 'Missing required fields'
      local.status = 'error'
      render()
      return
    }

    local.status = 'loading'
    local.error = null
    render()

    try {
      const res = await fetch(KLAVIYO_BACK_IN_STOCK_ENDPOINT(companyId), {
        method: 'POST',
        headers: {
          revision: KLAVIYO_API_REVISION,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: { type: 'profile', attributes: { email: local.email } }
              },
              channels: ['EMAIL']
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`
                }
              }
            }
          }
        })
      })

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}))
        const detail =
          isObject(errorData) && typeof errorData.detail === 'string'
            ? errorData.detail
            : `HTTP ${res.status}: Back in stock signup failed`

        throw new Error(detail)
      }

      local.status = 'success'
    } catch (error) {
      local.error =
        error instanceof Error ? error.message : 'An unknown error occurred'
      local.status = 'error'
    }

    render()
  }

  render()

  return { el: el_, sync: render }
}

/**
 * The colour swatch dropdown that sits under the first option fieldset.
 *
 * @param {{
 *   getValues: () => object[],
 *   getSelectedName: () => string | null,
 *   isAvailable: (valueName: string) => boolean,
 *   onSelect: (valueName: string) => void,
 *   namespace: object
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createOptionSelector = ({
  getValues,
  getSelectedName,
  isAvailable,
  onSelect,
  namespace
}) => {
  let isOpen = false

  const image = el('span', {
    class: 'product_form_optionSelector__trigger__image'
  })
  const name = el('div', {
    class: 'product_form_optionSelector__trigger__name'
  })
  const finish = el('div', {
    class: 'product_form_optionSelector__trigger__finish'
  })
  const dropdown = el('div')

  const trigger = el(
    'button',
    {
      type: 'button',
      class: 'product_form_optionSelector__trigger',
      'aria-haspopup': 'listbox'
    },
    [
      el('div', { class: 'product_form_optionSelector__trigger__content' }, [
        name,
        finish
      ]),
      icon('selector-chevron')
    ]
  )

  trigger.addEventListener('click', () => {
    isOpen = !isOpen
    sync()
  })

  const el_ = el('div', { class: 'product_form_optionSelector' }, [
    trigger,
    dropdown
  ])

  const outsideClickListener = namespace.addDocumentEventListener(
    'mousedown',
    (event) => {
      if (
        isOpen &&
        event.target instanceof Node &&
        !el_.contains(event.target)
      ) {
        isOpen = false
        sync()
      }
    }
  )

  const select = (valueName) => {
    onSelect(valueName)
    isOpen = false
    sync()
  }

  const sync = () => {
    const values = getValues()
    const selectedName = getSelectedName()
    const selectedValue = values.find((value) => value.name === selectedName)
    const imageUrl = selectedValue?.swatch?.custom_image

    if (imageUrl) {
      image.style.backgroundImage = `url(${imageUrl})`
      if (!trigger.contains(image)) {
        trigger.prepend(image)
      }
    } else {
      image.remove()
    }

    name.textContent = selectedName ?? ''
    finish.textContent = selectedValue?.subtitle ?? ''
    trigger.setAttribute('aria-expanded', String(isOpen))

    if (!isOpen) {
      dropdown.replaceChildren()
      return
    }

    preserveFocus(el_, () => {
      dropdown.replaceChildren(
        el('div', { class: 'product_form_optionSelector__dropdown' }, [
          el(
            'ul',
            { role: 'listbox', class: 'product_form_optionSelector__list' },
            values.map((value) => {
              const valueName = value.name ?? ''
              const available = isAvailable(valueName)
              const valueImageUrl = value.swatch?.custom_image

              return el('li', {}, [
                el(
                  'button',
                  {
                    type: 'button',
                    role: 'option',
                    'aria-selected': String(selectedName === valueName),
                    'aria-disabled': String(!available),
                    class: 'product_form_optionSelector__item',
                    on: { click: () => select(valueName) }
                  },
                  [
                    valueImageUrl
                      ? el(
                          'span',
                          {
                            class: 'product_form_optionSelector__item__image'
                          },
                          [
                            el('span', {
                              class:
                                'product_form_optionSelector__item__image__bg',
                              style: {
                                backgroundImage: `url(${valueImageUrl})`
                              }
                            }),
                            available
                              ? null
                              : el('span', {
                                  class:
                                    'product_form_optionSelector__item__unavailable_indicator',
                                  'aria-hidden': 'true'
                                })
                          ]
                        )
                      : null,
                    el(
                      'div',
                      {
                        class: 'product_form_optionSelector__item__content'
                      },
                      [
                        el('div', {
                          class: 'product_form_optionSelector__item__name',
                          text: valueName
                        }),
                        el('div', {
                          class: 'product_form_optionSelector__item__finish',
                          text: value.subtitle ?? ''
                        })
                      ]
                    )
                  ]
                )
              ])
            })
          )
        ])
      )
    })
  }

  sync()

  return { el: el_, sync, destroy: () => outsideClickListener.remove() }
}

const normalizeFamily = (family) => {
  const splitFamily = family.split('/')[0]

  return splitFamily ? splitFamily.toLowerCase().replace(/\s+/g, '_') : ''
}

const familyMatches = (normalizedValue, normalizedFamily) => {
  if (!normalizedValue.includes(normalizedFamily)) {
    return false
  }

  if (normalizedValue.includes(`_${normalizedFamily}`)) {
    return false
  }

  if (normalizedValue.includes(`${normalizedFamily}_`)) {
    return false
  }

  return true
}

const isColorOption = (optionName) =>
  Boolean(optionName) && optionName.trim().toLowerCase() === 'color'

const isSkippedOptionName = (optionName) =>
  Boolean(optionName) &&
  SKIP_OPTION_NAMES.includes(optionName.trim().toLowerCase())

const getStyleModifier = (key) => STYLE_MODIFIERS[key]

const getSwatchImageUrl = (value) => value?.swatch?.custom_image ?? null

const optionInputId = (fieldsetId, value) => `${fieldsetId}-${value.id}`

/**
 * The option values as a radiogroup, with swatch images and unavailable
 * indicators.
 *
 * @param {{
 *   option: object,
 *   index: number,
 *   fieldsetId: string,
 *   legendId: string,
 *   values: object[],
 *   isSelected: (index: number, name: string | null) => boolean,
 *   isAvailable: (valueName: string, optionPosition: number) => boolean,
 *   onChange: (value: string, index: number) => void
 * }} input
 * @returns {HTMLElement}
 */
const optionValuesFieldset = ({
  option,
  index,
  fieldsetId,
  legendId,
  values,
  isSelected,
  isAvailable,
  onChange
}) => {
  const optionName = option.name ?? ''

  return el(
    'fieldset',
    {
      id: fieldsetId,
      class: [
        'product_form_option',
        getStyleModifier(`option--${kebabCase(optionName.toLowerCase())}`)
      ]
        .filter(Boolean)
        .join(' ')
    },
    [
      el('legend', { id: legendId, class: 'product_form_option__legend' }, [
        optionName
      ]),
      el(
        'ul',
        {
          role: 'radiogroup',
          'aria-labelledby': legendId,
          class: [
            'product_form_option__values',
            getStyleModifier(
              `option__values--${kebabCase(optionName.toLowerCase())}`
            )
          ]
            .filter(Boolean)
            .join(' ')
        },
        values.map((value) => {
          const inputId = optionInputId(fieldsetId, value)
          const swatchImageUrl = getSwatchImageUrl(value)
          const available = isAvailable(value.name ?? '', option.position || 0)

          return el('li', { class: 'product_form_option__value' }, [
            el('input', {
              class: 'product_form_option__input',
              id: inputId,
              type: 'radio',
              name: `option-${optionName}`,
              value: value.name ?? '',
              checked: isSelected(index, value.name),
              required: true,
              'data-option-index': index,
              'aria-label': getLocaleString(
                'tvg_snippets.core_react_product_form.option_description',
                {
                  option_name: optionName,
                  value_name: value.name || ''
                }
              ),
              on: {
                change: (event) => {
                  if (event.target instanceof HTMLInputElement) {
                    onChange(event.target.value, index)
                  }
                }
              }
            }),
            el(
              'label',
              {
                for: inputId,
                class: [
                  'product_form_option__label',
                  swatchImageUrl ? 'product_form_option__label__image' : ''
                ]
                  .filter(Boolean)
                  .join(' ')
              },
              swatchImageUrl
                ? [
                    el(
                      'span',
                      {
                        class: 'product_form_option__image',
                        style: { backgroundImage: `url(${swatchImageUrl})` }
                      },
                      [el('span', { class: 'visually-hidden' }, [value.name])]
                    ),
                    available
                      ? null
                      : el('span', {
                          class: 'product_form_option__unavailable_indicator',
                          'aria-hidden': 'true'
                        })
                  ]
                : [value.name]
            )
          ])
        })
      )
    ]
  )
}

/**
 * The size-aware colour filtering, shared by the PDP and quickview options
 * regions.
 *
 * A colour × size product can have colours that are only stocked in one size.
 * Those variants do not exist, so their swatch-image metafield is missing. The
 * colour option is filtered to the colours that have a variant in the currently
 * picked size, so a colour that is not stocked is removed (and re-added when a
 * size that stocks it is picked). When the picked colour is removed by a size
 * change, the first colour still stocked in that size becomes selected. Changing
 * colour never filters or disables the size options.
 *
 * @param {{
 *   getState: () => object,
 *   optionsWithMultipleValues: () => object[],
 *   useCustomSizes: () => boolean
 * }} scope
 * @returns {{
 *   sizeOption: () => object | null,
 *   selectedValueForOption: (option: object) => string | null,
 *   isValueInSelectedSize: (value: object, option: object) => boolean,
 *   reconcileColorForSelectedSize: (selectedOptions: string[]) => string[]
 * }}
 */
const createSizeAwareColorFilter = ({
  getState,
  optionsWithMultipleValues,
  useCustomSizes
}) => {
  const isVariantAvailable = (valueName, optionPosition) =>
    (getState().selectedProduct?.variants ?? []).some(({ variant }) => {
      const variantOptionValue = variant.options[(optionPosition || 1) - 1]

      return variantOptionValue === valueName && variant.available === true
    })

  /**
   * The rendered size option, when the product has one. Custom-size products
   * hide the real size option and use the cross-sell links instead, so there is
   * nothing to filter the colours against in that case.
   *
   * @returns {object | null}
   */
  const sizeOption = () => {
    if (useCustomSizes()) {
      return null
    }

    return (
      optionsWithMultipleValues().find((option) =>
        isSkippedOptionName(option.name)
      ) ?? null
    )
  }

  /**
   * The value currently picked for an option. Selections are stored by the
   * option's index in `optionsWithMultipleValues()` (the same index the option
   * fieldsets use), not by its 1-based position.
   */
  const selectedValueForOption = (option) => {
    const index = optionsWithMultipleValues().indexOf(option)

    return index === -1 ? null : (getState().selectedOptions?.[index] ?? null)
  }

  /**
   * Whether any variant combines a colour with a size. A colour that only ships
   * in one size simply has no variant for the other, which is what makes its
   * swatch image (a variant metafield) missing.
   *
   * @param {string} valueName
   * @param {number} optionPosition - The colour option's position.
   * @param {number} sizePosition - The size option's position.
   * @param {string} sizeName
   * @returns {boolean}
   */
  const variantExistsForValueInSize = (
    valueName,
    optionPosition,
    sizePosition,
    sizeName
  ) =>
    (getState().selectedProduct?.variants ?? []).some(
      ({ variant }) =>
        variant.options[(optionPosition || 1) - 1] === valueName &&
        variant.options[(sizePosition || 1) - 1] === sizeName
    )

  /**
   * Whether a value should stay rendered for the currently selected size. Only
   * the colour option is constrained; every other option (the size included) is
   * always kept. Products with no size option, or with no size picked yet, are
   * not filtered either.
   *
   * @param {object} value
   * @param {object} option - The colour option.
   * @returns {boolean}
   */
  const isValueInSelectedSize = (value, option) => {
    if (!isColorOption(option.name)) {
      return true
    }

    const size = sizeOption()

    if (!size) {
      return true
    }

    const sizeName = selectedValueForOption(size)

    if (sizeName === null) {
      return true
    }

    return variantExistsForValueInSize(
      value.name ?? '',
      option.position || 1,
      size.position || 1,
      sizeName
    )
  }

  /**
   * The first colour that exists in the currently selected size. Available
   * variants win; when every matching variant is sold out the first one that
   * exists is used, so the selection still points at a real variant.
   *
   * @param {object} option - The colour option.
   * @returns {string | null}
   */
  const firstColorValueInSelectedSize = (option) => {
    const size = sizeOption()

    if (!size) {
      return null
    }

    const sizeName = selectedValueForOption(size)
    const sizePosition = size.position || 1
    const optionPosition = option.position || 1
    const values = option.values ?? []

    const inSize = values.filter((value) =>
      variantExistsForValueInSize(
        value.name ?? '',
        optionPosition,
        sizePosition,
        sizeName ?? ''
      )
    )

    const available = inSize.find((value) =>
      isVariantAvailable(value.name ?? '', optionPosition)
    )

    return (available ?? inSize[0])?.name ?? null
  }

  /**
   * Rewrites the colour selection when the picked size removes it: the value is
   * gone from the rendered list, so the first colour still stocked in that size
   * is selected instead. Changing colour never touches the size (see
   * `isValueInSelectedSize`, which only filters colours).
   *
   * @param {string[]} selectedOptions
   * @returns {string[]}
   */
  const reconcileColorForSelectedSize = (selectedOptions) => {
    const size = sizeOption()

    if (!size) {
      return selectedOptions
    }

    const options = optionsWithMultipleValues()
    const colorOption = options.find(
      (option) =>
        isColorOption(option.name) && (option.values?.length ?? 0) > 0
    )

    if (!colorOption) {
      return selectedOptions
    }

    const colorIndex = options.indexOf(colorOption)
    const sizeIndex = options.indexOf(size)
    const colorName = selectedOptions[colorIndex]
    const sizeName = selectedOptions[sizeIndex] ?? ''

    if (
      colorName &&
      variantExistsForValueInSize(
        colorName,
        colorOption.position || 1,
        size.position || 1,
        sizeName
      )
    ) {
      return selectedOptions
    }

    const replacement = firstColorValueInSelectedSize(colorOption)

    if (!replacement || replacement === colorName) {
      return selectedOptions
    }

    const next = [...selectedOptions]
    next[colorIndex] = replacement

    return next
  }

  /**
   * The size filter that can never empty a list: a size with no matching values
   * would give the shopper nothing to pick.
   *
   * @param {object[]} values
   * @param {object} option
   * @returns {object[]}
   */
  const valuesInSelectedSize = (values, option) => {
    const inSize = values.filter((value) => isValueInSelectedSize(value, option))

    return inSize.length > 0 ? inSize : values
  }

  return {
    sizeOption,
    selectedValueForOption,
    isValueInSelectedSize,
    valuesInSelectedSize,
    reconcileColorForSelectedSize
  }
}

/**
 * The PDP options region: one fieldset per option, plus the swatch family and
 * undertone tabs in front of the first option and the colour swatch dropdown
 * under it.
 *
 * @param {{
 *   data: object,
 *   idPrefix: string,
 *   getState: () => object,
 *   setSelectedOptions: (options: string[]) => void,
 *   namespace: object
 * }} scope
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createPdpOptionsRegion = ({
  data,
  idPrefix,
  getState,
  setSelectedOptions,
  namespace
}) => {
  let selectedSwatchFamily = 'All'
  let selectedUndertone = 'All'
  let signature = null
  let selector = null

  const el_ = el('div')

  const swatchFamilyOptions = () =>
    data.swatch_families?.[data.swatch_type ?? 'none'] || []

  const optionsWithMultipleValues = () =>
    (getState().selectedProduct?.metafields?.options_with_values ?? []).filter(
      (option) => option.values && option.values.length > 1
    )

  const useCustomSizes = () =>
    Boolean(getState().selectedProduct?.metafields?.use_custom_sizes)

  const isSelected = (index, option) =>
    Boolean(option && getState().selectedOptions?.[index] === option)

  const getVariantsWithOptionValue = (valueName, optionPosition, undertone) => {
    const variants = getState().selectedProduct?.variants ?? []

    return variants.filter(({ variant, metafields }) => {
      const variantOptionValue = variant.options[(optionPosition || 0) - 1]

      if (variantOptionValue !== valueName) {
        return false
      }

      return undertone ? metafields?.undertone === undertone : true
    })
  }

  const isVariantAvailable = (valueName, optionPosition) =>
    (getState().selectedProduct?.variants ?? []).some(({ variant }) => {
      const variantOptionValue = variant.options[(optionPosition || 1) - 1]

      return variantOptionValue === valueName && variant.available === true
    })

  const {
    sizeOption,
    selectedValueForOption,
    isValueInSelectedSize,
    valuesInSelectedSize,
    reconcileColorForSelectedSize
  } = createSizeAwareColorFilter({
    getState,
    optionsWithMultipleValues,
    useCustomSizes
  })

  const isWithinFamilyOrAlternative = (
    valueName,
    optionPosition,
    normalizedFamily
  ) => {
    if (familyMatches(normalizeFamily(valueName), normalizedFamily)) {
      return true
    }

    return getVariantsWithOptionValue(valueName, optionPosition).some(
      (variant) => {
        const alternativeFamilies = [
          ...(variant.metafields?.alternative_families || []),
          variant.metafields?.shade_family || ''
        ].filter(isPresent)

        return alternativeFamilies.some((altFamily) =>
          familyMatches(normalizeFamily(altFamily), normalizedFamily)
        )
      }
    )
  }

  const matchesUndertone = (valueName, optionPosition, undertone) =>
    getVariantsWithOptionValue(valueName, optionPosition, undertone).length > 0

  /**
   * Whether a value sits inside the family picked in the shade tabs. `All`
   * matches every value.
   *
   * @param {object} value
   * @param {object} option
   * @returns {boolean}
   */
  const isWithinSelectedFamily = (value, option) => {
    const normalizedFamily = normalizeFamily(selectedSwatchFamily)

    return (
      normalizedFamily === 'all' ||
      isWithinFamilyOrAlternative(
        value.name ?? '',
        option.position || 0,
        normalizedFamily
      )
    )
  }

  /**
   * Whether a value passes both shade filters.
   *
   * @param {object} value
   * @param {object} option
   * @returns {boolean}
   */
  const isAllowedValue = (value, option) =>
    isWithinSelectedFamily(value, option) &&
    (selectedUndertone === 'All' ||
      matchesUndertone(
        value.name ?? '',
        option.position || 0,
        selectedUndertone
      ))

  /**
   * Moves any option whose current value no longer passes `isAllowed` to the
   * first value that does.
   *
   * React held both filters in state, so the region re-rendered on every tab
   * click. They are closure variables here, so the re-render is requested
   * explicitly.
   *
   * @param {(value: object, option: object) => boolean} isAllowed
   */
  const applyFilters = (isAllowed) => {
    const currentState = getState()
    const newSelectedOptions = [...(currentState.selectedOptions ?? [])]
    let hasChanges = false

    // The colour option is also constrained by the picked size, so a value the
    // shade filters allow but the size does not stock is not a valid fallback.
    const isAllowedAndInSize = (value, option) =>
      isAllowed(value, option) && isValueInSelectedSize(value, option)

    for (const [index, option] of optionsWithMultipleValues().entries()) {
      const currentValue = currentState.selectedOptions?.[index]

      if (!currentValue) {
        continue
      }

      const optionValues = option.values ?? []
      const isCurrentValueAllowed = optionValues.some(
        (value) => value.name === currentValue && isAllowedAndInSize(value, option)
      )

      if (isCurrentValueAllowed) {
        continue
      }

      const firstAllowedValue = optionValues.find((value) =>
        isAllowedAndInSize(value, option)
      )

      if (firstAllowedValue?.name) {
        newSelectedOptions[index] = firstAllowedValue.name
        hasChanges = true
      }
    }

    if (hasChanges) {
      setSelectedOptions(newSelectedOptions)
    } else {
      render()
    }
  }

  /**
   * Picks a shade family, which also clears the undertone filter.
   *
   * @param {string} family
   */
  const handleSwatchFamilyChange = (family) => {
    selectedSwatchFamily = family
    selectedUndertone = 'All'

    applyFilters(isWithinSelectedFamily)
  }

  /**
   * Applies an undertone inside the current family.
   *
   * `All` short-circuits: the React version left the selection alone when the
   * undertone was cleared, so the family is not re-checked here either.
   *
   * @param {string} undertone
   */
  const handleUndertoneChange = (undertone) => {
    selectedUndertone = undertone

    applyFilters(
      (value, option) =>
        undertone === 'All' ||
        (isWithinSelectedFamily(value, option) &&
          matchesUndertone(value.name ?? '', option.position || 0, undertone))
    )
  }

  const handleChange = (value, index) => {
    if (!value || index === undefined) {
      return
    }

    const selectedOptions = [...(getState().selectedOptions ?? [])]
    selectedOptions[index] = value

    // A size change can remove the picked colour (its variant does not exist in
    // that size), in which case the first colour still stocked in the size is
    // selected. A colour change leaves the size alone.
    setSelectedOptions(reconcileColorForSelectedSize(selectedOptions))
  }

  /**
   * The option's values that pass the shade filters and, for the colour option,
   * exist in the currently selected size, with their subtitle.
   */
  const filteredValuesFor = (option) => {
    const allowed = (option.values ?? []).filter((value) =>
      isAllowedValue(value, option)
    )
    const values = valuesInSelectedSize(allowed, option)

    return values.map((value) => {
      const variants = getVariantsWithOptionValue(
        value.name ?? '',
        option.position || 0,
        selectedUndertone !== 'All' ? selectedUndertone : undefined
      )

      return {
        ...value,
        subtitle: variants[0]?.metafields?.extra_title_copy || ''
      }
    })
  }

  const undertoneOptionsFor = (option) => {
    const undertoneOptions = ['All']

    if (swatchFamilyOptions().length === 0) {
      return undertoneOptions
    }

    const normalizedSelectedSwatchFamily = normalizeFamily(selectedSwatchFamily)
    const variants = getState().selectedProduct?.variants ?? []
    const familyFilteredVariants =
      normalizedSelectedSwatchFamily === 'all'
        ? variants
        : variants.filter(({ variant, metafields }) => {
            const optionValue = variant.options[(option.position || 0) - 1]

            if (
              familyMatches(
                normalizeFamily(optionValue ?? ''),
                normalizedSelectedSwatchFamily
              )
            ) {
              return true
            }

            const alternativeFamilies = [
              ...(metafields?.alternative_families || []),
              metafields?.shade_family || ''
            ].filter(isPresent)

            return alternativeFamilies.some((altFamily) =>
              familyMatches(
                normalizeFamily(altFamily),
                normalizedSelectedSwatchFamily
              )
            )
          })

    const uniqueUndertones = new Set()

    for (const { metafields } of familyFilteredVariants) {
      if (metafields?.undertone) {
        uniqueUndertones.add(metafields.undertone)
      }
    }

    undertoneOptions.push(...Array.from(uniqueUndertones).sort())

    return undertoneOptions
  }

  const tabs = (values, getActive, onSelect, prefix) =>
    el('div', { class: 'product_form_swatchFamilies' }, [
      el(
        'ul',
        { role: 'tablist', class: 'product_form_swatchFamilies__list' },
        values.map((value) =>
          el('li', { role: 'presentation' }, [
            el(
              'button',
              {
                role: 'tab',
                type: 'button',
                id: `${idPrefix}${prefix}-${kebabCase(value)}`,
                'aria-selected': String(getActive() === value),
                class: [
                  'product_form_swatchFamilies__option',
                  getActive() === value
                    ? 'product_form_swatchFamilies__option--active'
                    : ''
                ]
                  .filter(Boolean)
                  .join(' '),
                on: { click: () => onSelect(value) }
              },
              [value]
            )
          ])
        )
      )
    ])

  const render = () => {
    selector?.destroy()
    selector = null

    const options = optionsWithMultipleValues()
    const hasVisibleOptions = options.some(
      (option) => !(useCustomSizes() && isSkippedOptionName(option.name))
    )

    if (!hasVisibleOptions) {
      el_.replaceChildren()
      return
    }

    const children = [
      el('legend', { class: 'product_form_options__legend' }, [
        getLocaleString('tvg_snippets.core_react_product_form.product_options')
      ])
    ]

    for (const [index, option] of options.entries()) {
      if (useCustomSizes() && isSkippedOptionName(option.name)) {
        continue
      }

      const optionName = option.name ?? ''
      const fieldsetId = `${idPrefix}${kebabCase(`option-${optionName}-${index}`)}`
      const legendId = `${fieldsetId}-legend`
      const filteredValues = filteredValuesFor(option)
      const undertoneOptions = undertoneOptionsFor(option)

      if (index === 0 && swatchFamilyOptions().length > 0) {
        children.push(
          el(
            'fieldset',
            { id: `${idPrefix}shades-fieldset`, class: 'product_form_option' },
            [
              el(
                'legend',
                {
                  id: `${idPrefix}shades-legend`,
                  class: 'product_form_option__legend'
                },
                [getLocaleString('products.product.shades_label')]
              ),
              tabs(
                swatchFamilyOptions(),
                () => selectedSwatchFamily,
                handleSwatchFamilyChange,
                'shades'
              )
            ]
          )
        )

        if (
          undertoneOptions.length > 1 &&
          normalizeFamily(selectedSwatchFamily) !== 'all'
        ) {
          children.push(
            el(
              'fieldset',
              {
                id: `${idPrefix}undertones-fieldset`,
                class: 'product_form_option'
              },
              [
                el(
                  'legend',
                  {
                    id: `${idPrefix}undertones-legend`,
                    class: 'product_form_option__legend'
                  },
                  [getLocaleString('products.product.undertones_label')]
                ),
                tabs(
                  undertoneOptions,
                  () => selectedUndertone,
                  handleUndertoneChange,
                  'undertones'
                )
              ]
            )
          )
        }
      }

      children.push(
        optionValuesFieldset({
          option,
          index,
          fieldsetId,
          legendId,
          values: filteredValues,
          isSelected,
          isAvailable: isVariantAvailable,
          onChange: handleChange
        })
      )

      if (
        index === 0 &&
        isColorOption(optionName) &&
        filteredValues.length > 0 &&
        filteredValues.some((value) => Boolean(getSwatchImageUrl(value)))
      ) {
        selector = createOptionSelector({
          getValues: () => filteredValuesFor(option),
          getSelectedName: () => getState().selectedOptions?.[index] ?? null,
          isAvailable: (valueName) =>
            isVariantAvailable(valueName, option.position || 0),
          onSelect: (value) => handleChange(value, index),
          namespace
        })

        children.push(selector.el)
      }
    }

    el_.replaceChildren(
      el('fieldset', { class: 'product_form_options' }, children)
    )
  }

  const renderSignature = () =>
    [
      getState().selectedProduct?.product?.id ?? '',
      useCustomSizes() ? '1' : '0',
      selectedSwatchFamily,
      selectedUndertone,
      // The colour values depend on the selected size: switching size has to
      // re-render them so colours missing from that size are removed (and
      // re-added when switching back).
      sizeOption() ? (selectedValueForOption(sizeOption()) ?? '') : ''
    ].join('\u0000')

  const syncSelection = () => {
    const selectedOptions = getState().selectedOptions ?? []

    for (const input of el_.querySelectorAll(
      'input[type="radio"][data-option-index]'
    )) {
      if (!(input instanceof HTMLInputElement)) {
        continue
      }

      input.checked =
        input.value === selectedOptions[Number(input.dataset.optionIndex)]
    }

    selector?.sync()
  }

  const sync = () => {
    const next = renderSignature()

    if (next !== signature) {
      signature = next
      preserveFocus(el_, render)
    }

    syncSelection()
  }

  sync()

  return { el: el_, sync }
}

/**
 * The quickview options region: one fieldset per option, with the selected
 * value appended to the legend. No swatch families or swatch dropdown.
 *
 * @param {{
 *   idPrefix: string,
 *   getState: () => object,
 *   setSelectedOptions: (options: string[]) => void
 * }} scope
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createQuickviewOptionsRegion = ({
  idPrefix,
  getState,
  setSelectedOptions
}) => {
  let signature = null

  const el_ = el('div')

  const optionsWithMultipleValues = () =>
    (getState().selectedProduct?.metafields?.options_with_values ?? []).filter(
      (option) => option.values && option.values.length > 1
    )

  const useCustomSizes = () =>
    Boolean(getState().selectedProduct?.metafields?.use_custom_sizes)

  const isSelected = (index, option) =>
    Boolean(option && getState().selectedOptions?.[index] === option)

  const isVariantAvailable = (valueName, optionPosition) =>
    (getState().selectedProduct?.variants ?? []).some(({ variant }) => {
      const variantOptionValue = variant.options[(optionPosition || 1) - 1]

      return variantOptionValue === valueName && variant.available === true
    })

  const {
    sizeOption,
    selectedValueForOption,
    valuesInSelectedSize,
    reconcileColorForSelectedSize
  } = createSizeAwareColorFilter({
    getState,
    optionsWithMultipleValues,
    useCustomSizes
  })

  /** The option's values that exist in the currently selected size. */
  const filteredValuesFor = (option) =>
    valuesInSelectedSize(option.values ?? [], option)

  const handleChange = (value, index) => {
    if (!value || index === undefined) {
      return
    }

    const selectedOptions = [...(getState().selectedOptions ?? [])]
    selectedOptions[index] = value

    // A size change can remove the picked colour (its variant does not exist in
    // that size), in which case the first colour still stocked in the size is
    // selected. A colour change leaves the size alone.
    setSelectedOptions(reconcileColorForSelectedSize(selectedOptions))
  }

  const render = () => {
    const options = optionsWithMultipleValues()
    const hasVisibleOptions = options.some(
      (option) => !(useCustomSizes() && isSkippedOptionName(option.name))
    )

    if (!hasVisibleOptions) {
      el_.replaceChildren()
      return
    }

    const children = [
      el('legend', { class: 'product_form_options__legend' }, [
        getLocaleString('tvg_snippets.core_react_product_form.product_options')
      ])
    ]

    for (const [index, option] of options.entries()) {
      if (useCustomSizes() && isSkippedOptionName(option.name)) {
        continue
      }

      const optionName = option.name ?? ''
      const fieldsetId = `${idPrefix}${kebabCase(`option-${optionName}-${index}`)}`
      const legendId = `${fieldsetId}-legend`

      const fieldset = optionValuesFieldset({
        option,
        index,
        fieldsetId,
        legendId,
        values: filteredValuesFor(option),
        isSelected,
        isAvailable: isVariantAvailable,
        onChange: handleChange
      })

      children.push(fieldset)
    }

    el_.replaceChildren(
      el('fieldset', { class: 'product_form_options' }, children)
    )
  }

  const renderSignature = () =>
    [
      getState().selectedProduct?.product?.id ?? '',
      useCustomSizes() ? '1' : '0',
      // The colour values depend on the selected size: switching size has to
      // re-render them so colours missing from that size are removed (and
      // re-added when switching back).
      sizeOption() ? (selectedValueForOption(sizeOption()) ?? '') : ''
    ].join('\u0000')

  const syncLegends = () => {
    for (const [index, option] of optionsWithMultipleValues().entries()) {
      const optionName = option.name ?? ''
      const fieldsetId = `${idPrefix}${kebabCase(`option-${optionName}-${index}`)}`
      const legend = el_.querySelector(`[id="${fieldsetId}-legend"]`)

      if (!(legend instanceof HTMLElement)) {
        continue
      }

      const selectedValue =
        getState().selectedOptions?.[index] ?? option.selected_value

      legend.replaceChildren(`${optionName}: `, String(selectedValue ?? ''))
    }
  }

  const syncSelection = () => {
    const selectedOptions = getState().selectedOptions ?? []

    for (const input of el_.querySelectorAll(
      'input[type="radio"][data-option-index]'
    )) {
      if (!(input instanceof HTMLInputElement)) {
        continue
      }

      input.checked =
        input.value === selectedOptions[Number(input.dataset.optionIndex)]
    }

    syncLegends()
  }

  const sync = () => {
    const next = renderSignature()

    if (next !== signature) {
      signature = next
      preserveFocus(el_, render)
    }

    syncSelection()
  }

  sync()

  return { el: el_, sync }
}

const selectedPriceHtml = (html, className) =>
  html ? el('span', className ? { class: className, html } : { html }) : null

/**
 * The purchase-type radiogroup: subscribe & save (with the plan dropdown and
 * the USP list) or a one-time purchase. Only rendered when the selected variant
 * enables selling plans.
 *
 * @param {{
 *   getState: () => object,
 *   updateSellingPlanState: (planId: number | null) => void,
 *   namespace: object
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createSellingPlans = ({ getState, updateSellingPlanState }) => {
  const uniqueId = `selling-plan-options-${Math.random().toString(36).slice(2, 11)}`
  let signature = null

  const el_ = el('div')

  const allSellingPlans = () =>
    getState().selectedProduct?.product?.selling_plan_groups?.flatMap(
      (group) => group.selling_plans
    ) ?? []

  const enableSellingPlans = () =>
    getState().selectedVariant?.metafields?.enable_selling_plans ?? false

  const handleToggle = (type) => {
    if (type === 'onetime') {
      updateSellingPlanState(null)
    } else {
      updateSellingPlanState(allSellingPlans()[0]?.id ?? null)
    }
  }

  const render = () => {
    if (!enableSellingPlans()) {
      el_.replaceChildren()
      return
    }

    const state = getState()
    const subscriptionId = `${uniqueId}-subscription`
    const onetimeId = `${uniqueId}-onetime`
    const selectId = `${uniqueId}-select`
    const fieldsetId = `${uniqueId}-fieldset`

    const subscriptionUsps = [1, 2, 3, 4].map((index) =>
      getLocaleString(`products.product.usp_${index}`)
    )

    const selectedAllocation =
      state.selectedSellingPlanAllocation ??
      state.selectedVariant?.metafields?.selling_plan_allocations?.[0] ??
      null

    const savingsPercentage = selectedAllocation
      ? getSellingPlanAllocationSavingsPercentage({
          sellingPlanAllocation: selectedAllocation
        })
      : 0

    const subscriptionPrice = getSelectedPrice({
      selectedVariant: state.selectedVariant,
      purchaseType: 'subscription',
      selectedSellingPlanAllocation: selectedAllocation,
      quantity: state.quantity
    })

    const onetimePrice = getSelectedPrice({
      selectedVariant: state.selectedVariant,
      purchaseType: 'onetime',
      selectedSellingPlanAllocation: null,
      quantity: state.quantity
    })

    const sellingPlans = allSellingPlans()
    const selectedPlanId =
      state.selectedSellingPlanId ?? sellingPlans[0]?.id ?? null

    // A native `<select>`: the options carry nothing but a name, so the custom
    // listbox only bought a styled open list at the cost of hand-written
    // keyboard handling.
    const planSelect = el(
      'select',
      {
        id: selectId,
        class: 'product_form_sellingPlanOptions__select',
        'aria-label': getLocaleString(
          'products.product.select_subscription_plan'
        ),
        disabled: state.purchaseType !== 'subscription',
        on: {
          change: (event) => {
            if (event.target instanceof HTMLSelectElement) {
              updateSellingPlanState(Number(event.target.value))
            }
          }
        }
      },
      sellingPlans.map((plan) =>
        el('option', {
          value: String(plan.id),
          text: plan.name,
          selected: plan.id === selectedPlanId
        })
      )
    )

    const subscriptionOption = el(
      'div',
      {
        class: [
          'product_form_sellingPlanOptions__option',
          state.purchaseType === 'subscription' ? 'is-active' : ''
        ]
          .filter(Boolean)
          .join(' '),
        on: {
          click: () => {
            if (getState().purchaseType !== 'subscription') {
              handleToggle('subscription')
            }
          }
        }
      },
      [
        el(
          'label',
          {
            for: subscriptionId,
            'aria-describedby': `${subscriptionId}-description`,
            class: 'product_form_sellingPlanOptions__label'
          },
          [
            el('input', {
              id: subscriptionId,
              type: 'radio',
              name: 'purchase-type',
              value: 'subscription',
              checked: state.purchaseType === 'subscription',
              class: 'product_form_sellingPlanOptions__input',
              on: {
                change: () => {
                  handleToggle('subscription')
                }
              }
            }),
            el('span', { class: 'product_form_sellingPlanOptions__name' }, [
              getLocaleString('products.product.subscribe_and_save'),
              el(
                'button',
                {
                  type: 'button',
                  class:
                    'product_form_sellingPlanOptions__icon js-offcanvas-trigger',
                  'aria-label': getLocaleString('accessibility.icons.help'),
                  'data-drawer-name': 'subscription-help'
                },
                [icon('info')]
              )
            ]),
            selectedAllocation
              ? el(
                  'span',
                  { class: 'product_form_sellingPlanOptions__price' },
                  [
                    el(
                      'span',
                      { class: 'product_form_sellingPlanOptions__savings' },
                      [
                        `${getLocaleString('products.product.save')} ${
                          savingsPercentage > 0 ? `${savingsPercentage}%` : ''
                        }`
                      ]
                    ),
                    selectedPriceHtml(
                      subscriptionPrice.price_html_with_quantity ??
                        subscriptionPrice.price_html
                    )
                  ]
                )
              : null
          ]
        ),
        el(
          'div',
          {
            id: `${subscriptionId}-description`,
            class: 'product_form_sellingPlanOptions__main',
            'aria-live': 'polite'
          },
          [
            el('div', { class: 'product_form_sellingPlanOptions__dropdown' }, [
              planSelect,
              icon('dropdown-chevron')
            ]),
            el(
              'div',
              {
                class: 'product_form_sellingPlanOptions__uniqueSellingPoints'
              },
              [
                el(
                  'div',
                  {
                    class:
                      'product_form_sellingPlanOptions__uniqueSellingPoints__title'
                  },
                  [
                    getLocaleString(
                      'products.product.how_monthly_subscriptions_work'
                    )
                  ]
                ),
                el(
                  'ul',
                  {},
                  subscriptionUsps.map((point, index) =>
                    el('li', { 'data-index': index }, [
                      el('span', {}, [icon('check'), point])
                    ])
                  )
                )
              ]
            )
          ]
        )
      ]
    )

    const onetimeOption = el(
      'div',
      {
        class: [
          'product_form_sellingPlanOptions__option',
          state.purchaseType === 'onetime' ? 'is-active' : ''
        ]
          .filter(Boolean)
          .join(' '),
        on: {
          click: () => {
            if (getState().purchaseType !== 'onetime') {
              handleToggle('onetime')
            }
          }
        }
      },
      [
        el(
          'label',
          {
            for: onetimeId,
            class: 'product_form_sellingPlanOptions__label'
          },
          [
            el('input', {
              id: onetimeId,
              type: 'radio',
              name: 'purchase-type',
              value: 'onetime',
              checked: state.purchaseType === 'onetime',
              class: 'product_form_sellingPlanOptions__input',
              on: {
                change: () => {
                  handleToggle('onetime')
                }
              }
            }),
            el('span', { class: 'product_form_sellingPlanOptions__name' }, [
              getLocaleString('products.product.one_time_purchase')
            ]),
            el('span', { class: 'product_form_sellingPlanOptions__price' }, [
              selectedPriceHtml(
                onetimePrice.price_html_with_quantity ?? onetimePrice.price_html
              )
            ])
          ]
        )
      ]
    )

    el_.replaceChildren(
      el(
        'div',
        {
          id: fieldsetId,
          role: 'radiogroup',
          class: 'product_form_sellingPlanOptions',
          'aria-label': getLocaleString('products.product.purchase_type')
        },
        [subscriptionOption, onetimeOption]
      )
    )
  }

  const renderSignature = () => {
    const state = getState()

    return [
      enableSellingPlans() ? '1' : '0',
      state.purchaseType,
      state.selectedSellingPlanId ?? '',
      state.selectedVariant?.variant.id ?? '',
      state.selectedSellingPlanAllocation?.selling_plan_id ?? '',
      state.quantity
    ].join('\u0000')
  }

  const sync = () => {
    const next = renderSignature()

    if (next === signature) {
      return
    }

    signature = next
    preserveFocus(el_, render)
  }

  sync()

  return { el: el_, sync }
}

/**
 * The full-size / travel-size cross-sell links. A link on the PDP, a
 * `quickview:load` button in the quickview.
 *
 * @param {{
 *   customSizes: object[],
 *   currentHandle: string,
 *   isQuickview: boolean,
 *   mainBus: object
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void } | null}
 */
const createCustomSizes = ({
  customSizes,
  currentHandle,
  isQuickview,
  mainBus
}) => {
  if (!customSizes || customSizes.length === 0) {
    return null
  }

  const links = customSizes
    .filter((size) => Boolean(size.handle))
    .map((size) => {
      const className = [
        'product_form_customSizes__link',
        size.handle === currentHandle
          ? 'product_form_customSizes__link--active'
          : ''
      ]
        .filter(Boolean)
        .join(' ')
      const label = `${size.title} - ${size.subtitle}`

      if (isQuickview) {
        return el(
          'button',
          {
            type: 'button',
            class: className,
            'aria-label': label,
            on: {
              click: () => {
                mainBus.send({
                  name: QUICKVIEW_LOAD,
                  source: { type: 'global' },
                  details: { handle: size.handle }
                })
              }
            }
          },
          [
            el('span', {
              class: 'product_form_customSizes__link__title',
              text: size.title
            })
          ]
        )
      }

      return el(
        'a',
        {
          href: getLocaleUrl({ path: `/products/${size.handle}` }),
          class: className,
          'aria-label': label
        },
        [
          el('span', {
            class: 'product_form_customSizes__link__title',
            text: size.title
          }),
          size.subtitle
            ? el('span', {
                class: 'product_form_customSizes__link__subtitle',
                text: size.subtitle
              })
            : null
        ]
      )
    })

  const el_ = el(
    'div',
    isQuickview ? { class: 'product_form_options__wrapper' } : {},
    [
      el(
        'p',
        {
          class: isQuickview ? 'u-vhide' : 'product_form_customSizes__title'
        },
        [getLocaleString('products.product.size')]
      ),
      el('div', { class: 'product_form_customSizes' }, links)
    ]
  )

  return { el: el_, sync: () => undefined }
}

/**
 * The section countdown. Ticks once a second and removes itself once the target
 * time has passed.
 *
 * @param {{
 *   timestamp: string | null | undefined,
 *   color: string,
 *   prefix: string | null | undefined,
 *   namespace: object
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createCountdown = ({ timestamp, color, prefix, namespace }) => {
  const wrapper = el('div')

  const timerElement = el('div', {
    role: 'timer',
    'aria-live': 'polite',
    'aria-atomic': 'true',
    style: color ? { color } : undefined
  })

  const element = el('p', { class: 'product_form_countdown' }, [
    prefix
      ? el('span', { class: 'product_form_countdown__prefix', text: prefix })
      : null,
    el('span', { class: 'u-showMd', text: '-' }),
    timerElement
  ])

  const calculateTimeLeft = () => {
    const targetDate = parseCountdownTimestamp(timestamp)

    if (!targetDate) {
      return null
    }

    const difference = targetDate.getTime() - new Date().getTime()

    if (difference <= 0) {
      return null
    }

    const totalHours = Math.floor(difference / (1000 * 60 * 60))

    return {
      days: Math.floor(totalHours / 24),
      hours: totalHours % 24,
      minutes: Math.floor((difference / (1000 * 60)) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    }
  }

  const format = (value) => value.toString().padStart(2, '0')

  const sync = () => {
    const timeLeft = calculateTimeLeft()

    if (!timeLeft) {
      wrapper.replaceChildren()
      return
    }

    if (!wrapper.contains(element)) {
      wrapper.replaceChildren(element)
    }

    timerElement.replaceChildren(
      ...(timeLeft.days > 0
        ? [
            el('span', {
              class: 'product_form_countdown__unit',
              text: `${timeLeft.days}d`
            }),
            el('span', { class: 'product_form_countdown__divider', text: ':' })
          ]
        : []),
      el('span', {
        class: 'product_form_countdown__unit',
        text: `${format(timeLeft.hours)}h`
      }),
      el('span', { class: 'product_form_countdown__divider', text: ':' }),
      el('span', {
        class: 'product_form_countdown__unit',
        text: `${format(timeLeft.minutes)}m`
      }),
      el('span', { class: 'product_form_countdown__divider', text: ':' }),
      el('span', {
        class: 'product_form_countdown__unit',
        text: `${format(timeLeft.seconds)}s`
      })
    )
  }

  sync()

  const timer = window.setInterval(sync, 1000)
  namespace.addCleanup(() => window.clearInterval(timer))

  return { el: wrapper, sync }
}

/**
 * The Okendo review stars shown in the quickview header.
 *
 * @param {{
 *   getProductData: () => object | null,
 *   getSelectedVariant: () => object | null
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createReviewStars = ({ getProductData, getSelectedVariant }) => {
  const empty = el('div', { class: REVIEW_STARS_CLASS.starsInner })
  const filled = el('div', { class: REVIEW_STARS_CLASS.starsInner })
  const content = el('div', { class: REVIEW_STARS_CLASS.content })

  const el_ = el('div', { class: REVIEW_STARS_CLASS.root }, [
    el('div', { class: REVIEW_STARS_CLASS.stars }, [empty, filled]),
    content
  ])

  // Built from DOM rather than markup: the `<use>` targets are the star symbols
  // `layout/theme.liquid` defines, so there is no static SVG to template.
  const starsSvg = (href) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')

    svg.setAttribute('height', '14')
    svg.setAttribute('viewBox', '0 0 81 14')
    svg.setAttribute('aria-hidden', 'true')
    svg.setAttribute('role', 'none')

    for (const x of STAR_POSITIONS) {
      const use = document.createElementNS('http://www.w3.org/2000/svg', 'use')

      use.setAttribute('x', String(x))
      use.setAttribute('href', `#${href}`)
      svg.append(use)
    }

    return svg
  }

  const sync = () => {
    const productData = getProductData()
    const selectedVariant = getSelectedVariant()
    const ratingCount = productData?.metafields?.okendo_rating_count ?? 0
    const ratingAverage = Number(
      productData?.metafields?.okendo_rating_average ?? 0
    )

    setChildren(empty, [starsSvg('review-star-empty')])
    setChildren(filled, [starsSvg('review-star-filled')])
    filled.style.width = `${(ratingAverage / 5) * 100}%`

    const countLabel = getLocaleString('products.product.review_stars.count', {
      count: String(ratingCount)
    })

    content.replaceChildren(
      el('span', { class: REVIEW_STARS_CLASS.average }, [
        `${ratingAverage} | `
      ]),
      selectedVariant
        ? el(
            'a',
            {
              class: REVIEW_STARS_CLASS.count,
              href: getLocaleUrl({
                path: `/products/${productData?.product?.handle}?variant=${selectedVariant?.variant?.id}#reviews`
              })
            },
            [countLabel]
          )
        : el('span', { class: REVIEW_STARS_CLASS.count }, [countLabel])
    )
  }

  sync()

  return { el: el_, sync }
}

/**
 * The quickview gallery: a `<scroll-carousel>` of the selected variant's images
 * with dot pagination. Replaces the former Swiper implementation.
 *
 * @param {{
 *   getState: () => object,
 *   getProductData: () => object | null,
 *   getTitle: () => string | null
 * }} input
 * @returns {{ el: HTMLElement, sync: () => void }}
 */
const createProductGallery = ({ getState, getProductData, getTitle }) => {
  let active = { key: '', index: 0 }
  let imagesKey = ''
  let track = null

  const el_ = el('div', { class: 'product_form_quickshop__gallery__wrapper' })

  const images = () =>
    getGalleryImages(
      getProductData()?.metafields?.media ?? null,
      getProductData(),
      getState().selectedVariant?.variant?.id ?? null,
      getTitle() ?? null
    )

  const filterSrcSet = (srcset, allowedWidths) =>
    srcset
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => {
        const descriptor = entry.split(/\s+/).pop() ?? ''

        return allowedWidths.some((width) => descriptor === `${width}w`)
      })
      .join(', ')

  const activeIndex = () => (active.key === imagesKey ? active.index : 0)

  const scrollToIndex = (index) => {
    const slide = track?.children[index]

    if (track != null && slide instanceof HTMLElement) {
      track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' })
    }
  }

  const updateBullets = () => {
    const bullets = el_.querySelectorAll(`.${GALLERY_BULLET_CLASS}`)

    bullets.forEach((bullet, index) => {
      bullet.setAttribute(
        'data-active',
        index === activeIndex() ? 'true' : 'false'
      )
    })
  }

  const render = () => {
    const list = images()

    if (list.length === 0) {
      track = null
      el_.replaceChildren()
      return
    }

    track = el(
      'div',
      {
        class: 'product_form_productGallery__swiper',
        'data-carousel-track': true
      },
      list.map((image, index) =>
        el('div', { class: 'product_form_productGallery__slide' }, [
          el('div', { class: 'product_form_productGallery__image' }, [
            el('img', {
              src: image.src ?? undefined,
              srcset: image.srcset
                ? filterSrcSet(image.srcset, GALLERY_SRCSET_WIDTHS)
                : undefined,
              sizes: '(max-width: 480px) 320px, 480px',
              alt: image.alt ?? getTitle() ?? '',
              width: image.width ?? undefined,
              height: image.height ?? undefined,
              loading: index === 0 ? 'eager' : 'lazy',
              decoding: 'async'
            })
          ])
        ])
      )
    )

    const pagination =
      list.length > 1
        ? el(
            'div',
            { class: 'product_form_productGallery__pagination' },
            list.map((image, index) =>
              el('button', {
                type: 'button',
                class: GALLERY_BULLET_CLASS,
                'data-active': index === activeIndex() ? 'true' : 'false',
                'aria-label': `Show image ${index + 1}`,
                on: { click: () => scrollToIndex(index) }
              })
            )
          )
        : null

    el_.replaceChildren(el('scroll-carousel', {}, [track, pagination]))
  }

  el_.addEventListener('carousel:change', (event) => {
    if (!(event instanceof CustomEvent)) {
      return
    }

    const index = event.detail?.index

    if (typeof index === 'number') {
      active = { key: imagesKey, index }
      updateBullets()
    }
  })

  const sync = () => {
    const key = images()
      .map((image) => image.src ?? '')
      .join('|')

    if (key !== imagesKey) {
      imagesKey = key
      render()
      return
    }

    updateBullets()
  }

  sync()

  return { el: el_, sync }
}

/* -------------------------------------------------------------------------- */
/* controllers                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Fetches the `?view=api` product JSON for a handle and validates its shape.
 *
 * @param {string} handle
 * @param {Record<string, string>} [params]
 * @returns {Promise<object>}
 */
const fetchProductJson = async (handle, params) => {
  const query = { ...params }

  if (
    query.view &&
    typeof query.view === 'string' &&
    !query.view.startsWith('api')
  ) {
    delete query.view
  }

  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries({ view: 'api', ...query })) {
    if (value !== null && value !== undefined) {
      searchParams.set(key, String(value))
    }
  }

  const path = atShopifyRoot(
    `/products/${handle}?${searchParams.toString()}`
  ).toString()
  const response = await fetch(path)

  if (!response.ok) {
    throw new Error(`Failed to fetch: ${response.statusText}`)
  }

  const product = parseProductJson(await response.json())

  if (product === null) {
    throw new Error('Validation failed for product JSON')
  }

  return product
}

/**
 * The PDP layout.
 *
 * @param {{ host: HTMLElement, data: object, namespace: object }} input
 * @returns {{ destroy: () => void }}
 */
const createPdpController = ({ host, data, namespace }) => {
  const mainBus = initMainBus()
  const cart = getCart()

  const formId = 'pdp-product-form'
  const idPrefix = `${kebabCase(formId)}-`
  const params = queryParamsToObject(window.location.search)

  const {
    handle,
    section_data: sectionData,
    ssr_html: ssrHtml,
    hydrate_data: hydrateData,
    enable_free_gifts: enableFreeGifts,
    free_gift_variant_ids: freeGiftVariantIds,
    render_findation_widget: renderFindationWidget,
    enable_countdown: enableCountdown,
    countdown_timestamp: countdownTimestamp,
    countdown_color: countdownColor,
    countdown_prefix: countdownPrefix,
    klaviyo: klaviyoSettings
  } = data

  const purchaseInfos = sectionData?.purchase_infos
  const pdpLinkBlocks = (sectionData?.blocks || []).filter(
    (block) => block.type === 'pdp_link'
  )

  const state = {
    isAddingToCart: false,
    purchaseType: 'onetime',
    selectedProduct: null,
    selectedVariant: null,
    selectedOptions: [],
    selectedSellingPlanId: null,
    selectedSellingPlanAllocation: null,
    quantity: 1
  }

  const backInStockLocal = { email: '', status: 'idle', error: null }

  let hydratedProduct = parseHydratedProduct(hydrateData)
  let fetchedProduct = null
  let isLoading = true
  let isStickyBarVisible = false
  let lastSentVariantId = null
  let bodyMode = null
  let form = null
  let optionsRegion = null
  let sellingPlansRegion = null
  let priceRegion = null
  let actionsRegion = null
  let actionsMode = null
  let actionsQuantity = null
  let actionsAtc = null
  let actionsWaitlist = null
  let backInStock = null
  let sticky = null
  let portalSpan = null

  // Icons live in the host's markup, so they must be collected before the host
  // is cleared.
  registerIcons(host)

  const root = el('div', { class: 'product_form_root' })
  host.replaceChildren(root)

  const idInput = el('input', {
    hidden: true,
    name: 'id',
    type: 'number',
    'aria-label': 'Variant ID'
  })
  const quantityInput = el('input', {
    hidden: true,
    name: 'quantity',
    type: 'number',
    'aria-label': getLocaleString('products.product.quantity')
  })
  const sellingPlanInput = el('input', {
    hidden: true,
    name: 'selling_plan',
    type: 'number',
    'aria-label': getLocaleString('products.product.selling_plan')
  })

  const productData = () => hydratedProduct ?? fetchedProduct

  const allSellingPlans = () =>
    state.selectedProduct?.product?.selling_plan_groups?.flatMap(
      (group) => group.selling_plans
    ) ?? []

  const hasProductOptions = () =>
    Boolean(
      !state.selectedProduct?.metafields?.has_only_default_variant &&
        state.selectedProduct?.product?.options &&
        state.selectedProduct.product.options.length > 0 &&
        state.selectedProduct.product.variants.length > 1
    )

  const hasSellingPlans = () =>
    Boolean(
      state.selectedProduct?.product?.selling_plan_groups &&
        state.selectedProduct.product.selling_plan_groups.length > 0
    )

  const shouldRenderCustomSizes = () =>
    Boolean(
      productData()?.metafields?.use_custom_sizes &&
        productData().metafields.custom_sizes?.length
    )

  const enableWaitlist = () =>
    Boolean(
      state.selectedProduct?.metafields?.enable_klaviyo_waitlist &&
        getWaitlistFormId(state.selectedProduct, state.selectedVariant)
    )

  const priceHtml = () => {
    const selectedPrice = getSelectedPrice({
      selectedVariant: state.selectedVariant,
      purchaseType: state.purchaseType,
      selectedSellingPlanAllocation: state.selectedSellingPlanAllocation,
      quantity: state.quantity
    })

    return selectedPrice.price_html_with_quantity ?? selectedPrice.price_html
  }

  const updateSellingPlanState = (planId) => {
    if (planId === null) {
      state.purchaseType = 'onetime'
      state.selectedSellingPlanId = null
      state.selectedSellingPlanAllocation = null
    } else {
      state.purchaseType = 'subscription'
      state.selectedSellingPlanId = planId
      state.selectedSellingPlanAllocation = getSelectedSellingPlanAllocation(
        planId,
        state.selectedVariant
      )
    }

    sync()
  }

  /**
   * Keeps the purchase type and selling plan consistent with the selected
   * variant. Replaces the two React effects in `SellingPlanOptions`.
   */
  const reconcileSellingPlan = () => {
    const enableSellingPlans =
      state.selectedVariant?.metafields?.enable_selling_plans ?? false

    if (!enableSellingPlans) {
      if (
        state.purchaseType === 'subscription' ||
        state.selectedSellingPlanId != null
      ) {
        state.purchaseType = 'onetime'
        state.selectedSellingPlanId = null
        state.selectedSellingPlanAllocation = null
      }

      return
    }

    if (state.purchaseType !== 'subscription') {
      return
    }

    const planId = allSellingPlans()[0]?.id ?? null
    const allocation = getSelectedSellingPlanAllocation(
      planId,
      state.selectedVariant
    )

    if (
      state.selectedSellingPlanId !== planId ||
      state.selectedSellingPlanAllocation !== allocation
    ) {
      state.selectedSellingPlanId = planId
      state.selectedSellingPlanAllocation = allocation
    }
  }

  const sendSelectedVariant = (derivedVariant) => {
    if (derivedVariant.variant.id === lastSentVariantId) {
      return
    }

    lastSentVariantId = derivedVariant.variant.id

    mainBus.send({
      name: PRODUCT_UPDATE_SELECTED_VARIANT,
      details: {
        selectedVariant: derivedVariant.variant,
        variantImageUrls: getVariantImageUrls(
          state.selectedProduct,
          derivedVariant.variant.id
        ),
        allVariantImageUrls: Array.from(
          new Set(
            state.selectedProduct.variants.flatMap(
              (item) => item.metafields?.variant_images ?? []
            )
          )
        ),
        product: state.selectedProduct.product,
        sellingPlanId: null,
        sellingPlanAfterPrice: null
      },
      source: { type: 'global' }
    })
  }

  const syncUrl = () => {
    if (isLoading || !state.selectedVariant?.variant?.id) {
      return
    }

    const url = new URL(window.location.href)
    url.searchParams.set('variant', state.selectedVariant.variant.id.toString())

    if (state.selectedSellingPlanId) {
      url.searchParams.set(
        'selling_plan',
        state.selectedSellingPlanId.toString()
      )
    } else {
      url.searchParams.delete('selling_plan')
    }

    window.history.replaceState({}, '', url.toString())
  }

  /** Recomputes the derived variant, the selling plan and the URL. */
  const derive = () => {
    const derivedVariant =
      getSelectedVariantFromOptions(
        state.selectedOptions,
        state.selectedProduct
      ) ?? state.selectedVariant

    if (derivedVariant && state.selectedProduct) {
      state.selectedVariant = derivedVariant
      sendSelectedVariant(derivedVariant)
      reconcileSellingPlan()
    }

    syncUrl()
  }

  const syncHiddenInputs = () => {
    idInput.value = String(state.selectedVariant?.variant?.id ?? '')
    quantityInput.value = String(state.quantity)

    if (state.purchaseType === 'subscription' && state.selectedSellingPlanId) {
      sellingPlanInput.value = String(state.selectedSellingPlanId)

      if (form != null && !form.contains(sellingPlanInput)) {
        form.append(sellingPlanInput)
      }
    } else {
      sellingPlanInput.remove()
    }
  }

  const renderPdpLinks = () =>
    el(
      'div',
      { class: 'product_form_pdpLinks' },
      pdpLinkBlocks.map((block) => {
        const iconSpan = el('span', {
          class: 'product_form_pdpLinks__link__icon',
          html: block.icon_html ?? ''
        })

        if (block.link_type === 'url') {
          return el(
            'a',
            {
              class: 'product_form_pdpLinks__link',
              href: block.url ?? '#'
            },
            [iconSpan, block.title]
          )
        }

        if (block.link_type === 'findation') {
          return el(
            'button',
            {
              type: 'button',
              class: 'product_form_pdpLinks__link',
              'data-findation-trigger': true,
              on: {
                click: () => {
                  if (window.Findation?.openWidget) {
                    window.Findation.openWidget()
                  }
                }
              }
            },
            [iconSpan, block.title]
          )
        }

        return null
      })
    )

  const renderPurchaseInfos = () =>
    el(
      'div',
      { class: 'product_form_purchaseInfos' },
      purchaseInfos.map((purchaseInfo) => {
        const text = purchaseInfo.text ?? ''

        if (!text) {
          return null
        }

        return el('div', { class: 'product_form_purchaseInfo' }, [
          purchaseInfo.icon
            ? el('img', {
                src: purchaseInfo.icon,
                alt: purchaseInfo.text ?? '',
                width: 14,
                height: 14,
                'aria-hidden': 'true',
                loading: 'lazy'
              })
            : null,
          el('span', { text })
        ])
      })
    )

  const buildForm = () => {
    actionsMode = null
    actionsQuantity = null
    actionsAtc = null
    actionsWaitlist = null
    backInStock = null

    form = el('form', {
      id: formId,
      method: 'post',
      action: getLocaleUrl({ path: '/cart/add' })
    })

    form.addEventListener('submit', handleSubmit, { capture: true })
    form.append(idInput, quantityInput)

    if (hasProductOptions()) {
      optionsRegion = createPdpOptionsRegion({
        data,
        idPrefix,
        getState: () => state,
        setSelectedOptions,
        namespace
      })

      const wrapper = el('div', { class: 'product_form_options__wrapper' }, [
        optionsRegion.el
      ])

      if (renderFindationWidget) {
        wrapper.append(
          el('div', { class: 'product_form_findationWidget' }, [
            el('div', { id: FINDATION_WIDGET_ID })
          ])
        )
      }

      if (pdpLinkBlocks.length > 0) {
        wrapper.append(renderPdpLinks())
      }

      form.append(wrapper)
    } else {
      optionsRegion = null
    }

    if (shouldRenderCustomSizes()) {
      const customSizes = createCustomSizes({
        customSizes: productData()?.metafields?.custom_sizes,
        currentHandle: productData()?.product?.handle,
        isQuickview: false,
        mainBus
      })

      if (customSizes) {
        form.append(customSizes.el)
      }
    }

    sellingPlansRegion = createSellingPlans({
      getState: () => state,
      updateSellingPlanState
    })

    if (hasSellingPlans()) {
      form.append(sellingPlansRegion.el)
    }

    priceRegion = el('div', { class: 'product_form_selectedPrice' })
    form.append(priceRegion)

    actionsRegion = el('div')
    form.append(actionsRegion)

    if (enableCountdown && countdownTimestamp) {
      const countdown = createCountdown({
        timestamp: countdownTimestamp,
        color: countdownColor || '#000',
        prefix: countdownPrefix,
        namespace
      })

      form.append(
        el('div', { class: 'product_form_countdown__wrapper' }, [countdown.el])
      )
    }

    if (purchaseInfos && purchaseInfos.length > 0) {
      form.append(renderPurchaseInfos())
    }

    return form
  }

  const renderActions = () => {
    const mode = enableWaitlist()
      ? 'waitlist'
      : state.selectedVariant?.variant?.available
        ? 'cart'
        : 'backInStock'

    if (mode !== actionsMode) {
      actionsMode = mode
      actionsQuantity = null
      actionsAtc = null
      actionsWaitlist = null
      backInStock = null

      if (mode === 'cart') {
        actionsQuantity = createQuantity({
          getQuantity: () => state.quantity,
          setQuantity
        })
        actionsAtc = createAddToCartButton({
          formId: null,
          getState: () => state,
          getPriceHtml: priceHtml
        })
        actionsRegion.replaceChildren(
          el('div', { class: 'product_form_actions' }, [
            actionsQuantity.el,
            actionsAtc.el
          ])
        )

        return
      }

      if (mode === 'waitlist') {
        actionsWaitlist = createWaitlist({
          getState: () => state,
          getIsMobile
        })
        setChildren(actionsRegion, [actionsWaitlist.el])

        return
      }

      backInStock = createBackInStock({
        klaviyoSettings,
        getVariantId: () =>
          state.selectedVariant?.variant?.id?.toString() ?? '',
        local: backInStockLocal
      })
      actionsRegion.replaceChildren(el('div', {}, [backInStock.el]))

      return
    }

    actionsQuantity?.sync()
    actionsAtc?.sync()
    actionsWaitlist?.sync()
    backInStock?.sync()
  }

  const scrollToForm = () => {
    if (form == null) {
      return
    }

    const rect = form.getBoundingClientRect()

    window.scrollTo({
      top: window.scrollY + rect.top - 100,
      behavior: 'smooth'
    })
  }

  /**
   * The sticky add-to-cart bar. Built once when a variant exists, then patched,
   * so its quantity input keeps focus while it is scrolled in and out.
   */
  const createSticky = () => {
    let actionsMode = null
    let quantity = null
    let atc = null
    let waitlist = null

    const infoTitle = el('p', {
      class: 'product_form_stickyAddToCart__info__title'
    })
    const selectorImage = el('span', {
      class: 'product_form_optionSelector__trigger__image'
    })
    const selectorName = el('div', {
      class: 'product_form_optionSelector__trigger__name'
    })
    const selectorFinish = el('div', {
      class: 'product_form_optionSelector__trigger__finish'
    })
    const selectorButton = el(
      'button',
      {
        type: 'button',
        class: 'product_form_stickyAddToCart__variantSelector',
        'aria-label': 'Scroll to variant selector',
        on: { click: scrollToForm }
      },
      [
        selectorImage,
        el('div', { class: 'product_form_optionSelector__trigger__content' }, [
          selectorName,
          selectorFinish
        ]),
        icon('sticky-chevron')
      ]
    )
    const actions = el('div', {
      class: 'product_form_stickyAddToCart__actions'
    })
    const actionsWrapper = el(
      'div',
      { class: 'product_form_stickyAddToCart__actions__wrapper' },
      [selectorButton, actions]
    )

    const el_ = el(
      'div',
      { class: 'product_form_stickyAddToCart', inert: '' },
      [
        el('div', { class: 'product_form_stickyAddToCart__info' }, [infoTitle]),
        actionsWrapper
      ]
    )

    const syncActions = () => {
      const mode = enableWaitlist()
        ? 'waitlist'
        : state.selectedVariant?.variant?.available
          ? 'cart'
          : 'backInStock'

      if (mode === actionsMode) {
        quantity?.sync()
        atc?.sync()
        waitlist?.sync()
        return
      }

      actionsMode = mode
      quantity = null
      atc = null
      waitlist = null

      if (mode === 'cart') {
        quantity = createQuantity({
          getQuantity: () => state.quantity,
          setQuantity
        })
        atc = createAddToCartButton({
          formId,
          getState: () => state,
          getPriceHtml: priceHtml
        })
        setChildren(actions, [quantity.el, atc.el])

        return
      }

      if (mode === 'waitlist') {
        waitlist = createWaitlist({
          getState: () => state,
          getIsMobile
        })
        setChildren(actions, [waitlist.el])

        return
      }

      actions.replaceChildren(
        el(
          'button',
          {
            type: 'button',
            class: 'product_form_stickyAddToCart__backInStockButton',
            on: { click: scrollToForm }
          },
          [
            el('span', {
              text: getLocaleString('products.back_in_stock.notify_me')
            })
          ]
        )
      )
    }

    const sync = () => {
      const metafields = state.selectedProduct?.metafields
      const firstOption = metafields?.options_with_values?.[0]
      const selectedFirstOptionName = state.selectedOptions?.[0] ?? ''
      const selectedFirstOptionValue = firstOption?.values?.find(
        (value) => value?.name === selectedFirstOptionName
      )
      const optionName =
        selectedFirstOptionName || state.selectedVariant?.variant.title || ''
      const optionSubtitle =
        state.selectedVariant?.metafields?.extra_title_copy || ''
      const optionImageUrl =
        selectedFirstOptionValue?.swatch?.custom_image || ''

      setChildren(infoTitle, [
        metafields?.title ?? '',
        metafields?.subtitle ? el('span', { text: metafields.subtitle }) : null
      ])

      if (optionImageUrl) {
        selectorImage.style.backgroundImage = `url(${optionImageUrl})`

        if (!selectorButton.contains(selectorImage)) {
          selectorButton.prepend(selectorImage)
        }

        if (!actionsWrapper.contains(selectorButton)) {
          actionsWrapper.prepend(selectorButton)
        }
      } else {
        selectorButton.remove()
      }

      selectorName.textContent = optionName
      selectorFinish.replaceChildren(
        ...(optionSubtitle ? [optionSubtitle] : [])
      )
      selectorFinish.hidden = !optionSubtitle

      syncActions()
    }

    const setVisible = (isVisible) => {
      el_.className = isVisible
        ? 'product_form_stickyAddToCart product_form_is-visible'
        : 'product_form_stickyAddToCart'

      if (isVisible) {
        el_.removeAttribute('inert')
      } else {
        el_.setAttribute('inert', '')
      }
    }

    sync()
    setVisible(isStickyBarVisible)

    return { el: el_, sync, setVisible }
  }

  const syncSticky = () => {
    if (!state.selectedVariant) {
      sticky?.el.remove()
      sticky = null
      return
    }

    if (sticky == null) {
      sticky = createSticky()
      root.append(sticky.el)
    }

    sticky.sync()
    sticky.setVisible(isStickyBarVisible)
  }

  const syncStickyVisibility = () => {
    document.body.setAttribute(
      'data-sticky-bar-visible',
      String(isStickyBarVisible)
    )
    sticky?.setVisible(isStickyBarVisible)
  }

  const syncPrice = () => {
    const html = priceHtml()
    priceRegion?.replaceChildren(...(html ? [el('span', { html })] : []))

    if (portalSpan) {
      portalSpan.innerHTML = html ?? ''
    }
  }

  const syncPortal = () => {
    const productId = state.selectedProduct?.product?.id
    const container = productId
      ? findOneElement(
          document,
          `[data-react-product-price-portal][data-product-id="${productId}"]`
        )
      : null

    if (container == null) {
      portalSpan = null
      return
    }

    if (portalSpan == null || portalSpan.parentElement !== container) {
      portalSpan = el('span')
      container.replaceChildren(portalSpan)
    }

    portalSpan.innerHTML = priceHtml() ?? ''
  }

  const syncBody = () => {
    const mode = isLoading || productData() == null ? 'loading' : 'form'

    if (mode === bodyMode) {
      return
    }

    bodyMode = mode

    for (const child of Array.from(root.children)) {
      if (child !== sticky?.el) {
        child.remove()
      }
    }

    if (mode === 'loading') {
      form = null
      optionsRegion = null
      sellingPlansRegion = null
      priceRegion = null
      actionsRegion = null
      actionsMode = null
      root.prepend(createLoading(ssrHtml))
      return
    }

    root.prepend(buildForm())
  }

  const sync = () => {
    syncBody()

    if (bodyMode !== 'form') {
      return
    }

    syncHiddenInputs()
    optionsRegion?.sync()
    sellingPlansRegion?.sync()
    syncPrice()
    renderActions()
    syncSticky()
    syncPortal()
  }

  const setSelectedOptions = (selectedOptions) => {
    state.selectedOptions = selectedOptions
    derive()
    sync()
  }

  const setQuantity = (quantity) => {
    state.quantity = quantity
    sync()
  }

  const onProductLoaded = (product) => {
    state.selectedProduct = product

    const initialVariant = getInitialVariant(product)
    state.selectedOptions = initialVariant?.variant.options ?? []
    state.selectedVariant = initialVariant
    state.purchaseType = 'onetime'
    state.selectedSellingPlanId = null
    state.selectedSellingPlanAllocation = null
    state.quantity = 1

    derive()
    sync()
    initFindationWidget()
  }

  const loadProduct = async () => {
    if (hydratedProduct != null) {
      isLoading = false
      onProductLoaded(hydratedProduct)
      return
    }

    if (!handle) {
      isLoading = false
      sync()
      return
    }

    try {
      fetchedProduct = await fetchProductJson(handle, params)
      isLoading = false
      onProductLoaded(fetchedProduct)
    } catch (error) {
      console.error(error)
      isLoading = false
      sync()
    }
  }

  const initFindationWidget = () => {
    if (
      isLoading ||
      productData() == null ||
      !hasProductOptions() ||
      !renderFindationWidget
    ) {
      return
    }

    let cancelled = false
    let attempts = 0

    const tryInit = () => {
      if (cancelled) {
        return
      }

      attempts += 1

      if (attempts > FINDATION_MAX_ATTEMPTS) {
        return
      }

      if (!document.getElementById(FINDATION_WIDGET_ID)) {
        window.setTimeout(tryInit, FINDATION_ATTEMPT_DELAY)
        return
      }

      if (!window.Findation?.init || typeof window.initWidget !== 'function') {
        window.setTimeout(tryInit, FINDATION_ATTEMPT_DELAY)
        return
      }

      window.initWidget()
    }

    tryInit()

    namespace.addCleanup(() => {
      cancelled = true
    })
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const target = event.currentTarget

    if (!(target instanceof HTMLFormElement)) {
      return
    }

    state.isAddingToCart = true
    sync()

    const run = async () => {
      const ajaxCartData = target.getAttribute('data-ajax-cart-disabled')
      const itemPayload = parseItemPayload(
        createRawItemPayload(readFormDataPairs(target))
      )

      if (itemPayload === null) {
        console.warn('Invalid item payload')
        throw new Error('Internal error: Invalid item payload')
      }

      let freeGifts = []

      if (enableFreeGifts && freeGiftVariantIds && itemPayload.selling_plan) {
        freeGifts = freeGiftVariantIds.map((variantId) => ({
          id: Number.parseInt(variantId, 10),
          quantity: 1,
          properties: { 'Free Gift': 'Offer' }
        }))
      }

      const addItemsResult = await cart.sendAsync({
        type: 'AddItems',
        payload: { items: [itemPayload, ...freeGifts] }
      })

      if (addItemsResult === 'busy') {
        console.warn('Cannot add item at the moment, try again later')
        state.isAddingToCart = false
        sync()
        return
      }

      if (ajaxCartData === 'disabled') {
        window.location.replace('/cart')
      } else {
        mainBus.send({
          name: OPEN_CART_DRAWER,
          details: null,
          source: { type: 'global' }
        })
      }

      state.isAddingToCart = false
      sync()
    }

    run().catch((error) => {
      console.error(error)

      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Unknown error'

      // The React version wrote this into a ref that was never attached to an
      // element, so a failed submit only ever reached the console.
      console.warn([message, ERROR_SUFFIX].join(' '))

      state.isAddingToCart = false
      sync()
    })
  }

  const initStickyObservers = () => {
    if (typeof IntersectionObserver === 'undefined') {
      return
    }

    let rootIntersecting = true
    let endIntersecting = false

    const updateVisible = () => {
      isStickyBarVisible = !rootIntersecting && !endIntersecting
      syncStickyVisibility()
    }

    const rootObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          rootIntersecting = entry.isIntersecting
          updateVisible()
        }
      },
      { threshold: 0.1 }
    )

    const endObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          endIntersecting = entry.isIntersecting
          updateVisible()
        }
      },
      { threshold: 0 }
    )

    rootObserver.observe(root)

    const footer = document.querySelector('footer')

    if (footer) {
      endObserver.observe(footer)
    }

    namespace.addCleanup(() => {
      rootObserver.disconnect()
      endObserver.disconnect()
      document.body.removeAttribute('data-sticky-bar-visible')
    })
  }

  const initFindationListener = () => {
    const handleFindationMessage = (event) => {
      if (
        !event.data ||
        typeof event.data !== 'object' ||
        event.data.eventName !== FINDATION_SEARCH_COMPLETE ||
        !('externalId' in event.data) ||
        !state.selectedProduct
      ) {
        return
      }

      const variantId = Number(event.data.externalId)

      if (!Number.isFinite(variantId)) {
        return
      }

      const matchedVariant = state.selectedProduct.variants.find(
        ({ variant }) => variant.id === variantId
      )

      if (!matchedVariant) {
        return
      }

      setSelectedOptions(matchedVariant.variant.options)
    }

    window.addEventListener('message', handleFindationMessage)
    namespace.addCleanup(() =>
      window.removeEventListener('message', handleFindationMessage)
    )
  }

  const getIsMobile = trackMobileQuery(namespace, () => sync())

  initStickyObservers()
  initFindationListener()
  sync()
  void loadProduct()

  return {
    destroy: () => {
      // The portal target lives outside the element; clear the span the element
      // injected so the page's own price markup is not left replaced.
      if (portalSpan?.parentElement != null) {
        portalSpan.parentElement.replaceChildren()
      }

      host.replaceChildren()
    }
  }
}

/**
 * The quickview layout. Loads the product when the drawer emits
 * `quickview:load`.
 *
 * @param {{ host: HTMLElement, data: object, namespace: object }} input
 * @returns {{ destroy: () => void }}
 */
const createQuickviewController = ({ host, data, namespace }) => {
  const mainBus = initMainBus()
  const cart = getCart()

  const formId = 'quickview-product-form'
  const idPrefix = `${kebabCase(formId)}-`

  const { ssr_html: ssrHtml, klaviyo: klaviyoSettings } = data

  const state = {
    isAddingToCart: false,
    purchaseType: 'onetime',
    selectedProduct: null,
    selectedVariant: null,
    selectedOptions: [],
    selectedSellingPlanId: null,
    selectedSellingPlanAllocation: null,
    quantity: 1
  }

  let handle = null
  let productData = null
  let isLoading = true
  let bodyMode = null
  let form = null
  let optionsRegion = null
  let actionsRegion = null
  let actionsMode = null
  let atc = null
  let waitlist = null
  let backInStock = null
  let reviewStars = null
  let gallery = null

  // Icons live in the host's markup, so they must be collected before the host
  // is cleared.
  registerIcons(host)

  const root = el('div', { class: 'product_form_quickviewRoot' })
  host.replaceChildren(root)

  const idInput = el('input', {
    hidden: true,
    name: 'id',
    type: 'number',
    'aria-label': 'Variant ID'
  })
  const quantityInput = el('input', {
    hidden: true,
    name: 'quantity',
    type: 'number',
    'aria-label': getLocaleString('products.product.quantity')
  })
  const sellingPlanInput = el('input', {
    hidden: true,
    name: 'selling_plan',
    type: 'number',
    'aria-label': getLocaleString('products.product.selling_plan')
  })

  const productTitle = () =>
    productData?.metafields?.title ?? productData?.product?.title

  const productSubtitle = () => productData?.metafields?.subtitle

  const hasReviews = () =>
    Boolean(
      productData?.metafields?.okendo_rating_average &&
        (productData?.metafields?.okendo_rating_count ?? 0) > 0
    )

  const hasProductOptions = () =>
    Boolean(
      state.selectedProduct &&
        state.selectedProduct.product.variants?.length > 1
    )

  const shouldRenderCustomSizes = () =>
    Boolean(
      productData?.metafields?.use_custom_sizes &&
        productData.metafields.custom_sizes?.length
    )

  const enableWaitlist = () =>
    Boolean(
      state.selectedProduct?.metafields?.enable_klaviyo_waitlist &&
        getWaitlistFormId(state.selectedProduct, state.selectedVariant)
    )

  const priceHtml = () => {
    const selectedPrice = getSelectedPrice({
      selectedVariant: state.selectedVariant,
      purchaseType: state.purchaseType,
      selectedSellingPlanAllocation: state.selectedSellingPlanAllocation,
      quantity: state.quantity
    })

    return selectedPrice.price_html_with_quantity ?? selectedPrice.price_html
  }

  const derive = () => {
    const derivedVariant =
      getSelectedVariantFromOptions(
        state.selectedOptions,
        state.selectedProduct
      ) ?? state.selectedVariant

    if (derivedVariant && derivedVariant !== state.selectedVariant) {
      state.selectedVariant = derivedVariant
    }
  }

  const setSelectedOptions = (selectedOptions) => {
    state.selectedOptions = selectedOptions
    derive()
    sync()
  }

  const syncHiddenInputs = () => {
    idInput.value = String(state.selectedVariant?.variant?.id ?? '')
    quantityInput.value = String(state.quantity)

    if (state.purchaseType === 'subscription' && state.selectedSellingPlanId) {
      sellingPlanInput.value = String(state.selectedSellingPlanId)

      if (form != null && !form.contains(sellingPlanInput)) {
        form.append(sellingPlanInput)
      }
    } else {
      sellingPlanInput.remove()
    }
  }

  const buildHeader = () => {
    const title = el('h1', { class: 'product_form_quickshop__header__title' }, [
      productTitle(),
      el(
        'button',
        {
          class: 'product_form_quickshop__header__close js-offcanvas-close',
          type: 'button',
          'aria-label': getLocaleString('accessibility.icons.close')
        },
        [
          el('span', { class: 'u-vhide' }, [
            getLocaleString('accessibility.icons.close')
          ]),
          el('span', { class: 'product_form_quickshop__header__icon' }, [
            icon('close')
          ]),
          el(
            'span',
            { class: 'product_form_quickshop__header__icon--mobile' },
            [icon('chevron-down')]
          )
        ]
      )
    ])

    const children = [title]

    if (productSubtitle()) {
      children.push(
        el('h2', {
          class: 'product_form_quickshop__header__subtitle',
          text: productSubtitle()
        })
      )
    }

    if (hasReviews()) {
      reviewStars = createReviewStars({
        getProductData: () => productData,
        getSelectedVariant: () => state.selectedVariant
      })
      children.push(
        el('div', { class: 'product_form_productReviews' }, [reviewStars.el])
      )
    } else {
      reviewStars = null
    }

    return el('header', { class: 'product_form_quickshop__header' }, children)
  }

  const buildForm = () => {
    actionsMode = null
    atc = null
    backInStock = null

    form = el('form', {
      id: formId,
      method: 'post',
      action: getLocaleUrl({ path: '/cart/add' })
    })

    form.addEventListener('submit', handleSubmit, { capture: true })
    form.append(idInput, quantityInput)

    if (shouldRenderCustomSizes()) {
      const customSizes = createCustomSizes({
        customSizes: productData?.metafields?.custom_sizes,
        currentHandle: productData?.product?.handle,
        isQuickview: true,
        mainBus
      })

      if (customSizes) {
        form.append(customSizes.el)
      }
    }

    if (hasProductOptions()) {
      optionsRegion = createQuickviewOptionsRegion({
        idPrefix,
        getState: () => state,
        setSelectedOptions
      })
      form.append(
        el('div', { class: 'product_form_options__wrapper' }, [
          optionsRegion.el
        ])
      )
    } else {
      optionsRegion = null
    }

    actionsRegion = el('div', { class: 'product_form_quickshop__actions' })
    form.append(actionsRegion)

    return el('div', { class: 'product_form_quickshop__form__wrapper' }, [form])
  }

  const renderActions = () => {
    const mode = enableWaitlist()
      ? 'waitlist'
      : state.selectedVariant?.variant?.available
        ? 'cart'
        : 'backInStock'

    const viewProductLink = () =>
      el(
        'a',
        {
          class: 'product_form_quickshop__actions__link',
          href: getLocaleUrl({
            path: `/products/${productData?.product?.handle}?variant=${state.selectedVariant?.variant?.id}`
          })
        },
        [getLocaleString('products.product.quickshop_view_product')]
      )

    if (mode !== actionsMode) {
      actionsMode = mode
      atc = null
      waitlist = null
      backInStock = null

      if (mode === 'cart') {
        atc = createAddToCartButton({
          formId: null,
          getState: () => state,
          getPriceHtml: priceHtml
        })
        setChildren(actionsRegion, [atc.el, viewProductLink()])

        return
      }

      if (mode === 'waitlist') {
        waitlist = createWaitlist({
          getState: () => state,
          getIsMobile
        })
        setChildren(actionsRegion, [waitlist.el, viewProductLink()])

        return
      }

      backInStock = createBackInStock({
        klaviyoSettings,
        getVariantId: () =>
          state.selectedVariant?.variant?.id?.toString() ?? '',
        local: { email: '', status: 'idle', error: null }
      })
      setChildren(actionsRegion, [backInStock.el, viewProductLink()])

      return
    }

    atc?.sync()
    waitlist?.sync()
    backInStock?.sync()
  }

  const syncBody = () => {
    const mode = isLoading || productData == null ? 'loading' : 'form'

    if (mode === bodyMode) {
      return
    }

    bodyMode = mode

    if (mode === 'loading') {
      form = null
      optionsRegion = null
      actionsRegion = null
      actionsMode = null
      gallery = null
      reviewStars = null
      root.replaceChildren(createLoading(ssrHtml))
      return
    }

    gallery = createProductGallery({
      getState: () => state,
      getProductData: () => productData,
      getTitle: () => productTitle()
    })

    root.replaceChildren(buildHeader(), gallery.el, buildForm())
  }

  const sync = () => {
    syncBody()

    if (bodyMode !== 'form') {
      return
    }

    syncHiddenInputs()
    optionsRegion?.sync()
    gallery?.sync()
    reviewStars?.sync()
    renderActions()
  }

  const onProductLoaded = (product) => {
    state.selectedProduct = product

    const initialVariant = getInitialVariant(product)
    state.selectedOptions = initialVariant?.variant.options ?? []
    state.selectedVariant = initialVariant
    state.purchaseType = 'onetime'
    state.selectedSellingPlanId = null
    state.selectedSellingPlanAllocation = null
    state.quantity = 1

    derive()
    sync()
  }

  const loadProduct = async () => {
    if (!handle) {
      isLoading = false
      sync()
      return
    }

    try {
      productData = await fetchProductJson(handle)
      isLoading = false
      onProductLoaded(productData)
    } catch (error) {
      console.error(error)
      isLoading = false
      sync()
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const target = event.currentTarget

    if (!(target instanceof HTMLFormElement)) {
      return
    }

    state.isAddingToCart = true
    sync()

    const run = async () => {
      const ajaxCartData = target.getAttribute('data-ajax-cart-disabled')
      const itemPayload = parseItemPayload(
        createRawItemPayload(readFormDataPairs(target))
      )

      if (itemPayload === null) {
        console.warn('Invalid item payload')
        throw new Error('Internal error: Invalid item payload')
      }

      const addItemsResult = await cart.sendAsync({
        type: 'AddItems',
        payload: { items: [itemPayload] }
      })

      if (addItemsResult === 'busy') {
        console.warn('Cannot add item at the moment, try again later')
        state.isAddingToCart = false
        sync()
        return
      }

      if (ajaxCartData === 'disabled') {
        window.location.replace('/cart')
      } else {
        mainBus.send({
          name: CORE_DRAWER_CLOSE,
          details: { drawer: 'quickview' },
          source: { type: 'global' }
        })
        mainBus.send({
          name: OPEN_CART_DRAWER,
          details: null,
          source: { type: 'global' }
        })
      }

      state.isAddingToCart = false
      sync()
    }

    run().catch((error) => {
      console.error(error)

      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Unknown error'

      console.warn([message, ERROR_SUFFIX].join(' '))

      state.isAddingToCart = false
      sync()
    })
  }

  mainBus.on(QUICKVIEW_LOAD).do((payload) => {
    handle = payload.details.handle
    isLoading = true
    syncBody()
    void loadProduct()
  })

  const getIsMobile = trackMobileQuery(namespace, () => sync())

  sync()
  void loadProduct()

  return {
    destroy: () => {
      host.replaceChildren()
    }
  }
}

/* -------------------------------------------------------------------------- */
/* element                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * @param {HTMLElement} host
 * @returns {unknown | undefined}
 */
const readDataScript = (host) => {
  const script = host.querySelector(DATA_SELECTOR)
  const raw = script?.textContent?.trim()

  if (!raw) {
    return undefined
  }

  try {
    return JSON.parse(raw)
  } catch (error) {
    console.error('Invalid product-form data', error)
    return undefined
  }
}

class ProductForm extends HTMLElement {
  #namespace = null
  #controller = null
  #observer = null
  #isMounted = false
  #data = null

  #stopObserving() {
    this.#observer?.disconnect()
    this.#observer = null
  }

  #tryInit() {
    if (!this.#isMounted || !this.isConnected) {
      return false
    }

    if (this.#controller != null) {
      return true
    }

    if (this.#data == null) {
      const raw = readDataScript(this)

      if (raw === undefined) {
        return false
      }

      const data = parseProductFormData(raw)

      if (data == null) {
        console.error('Invalid product-form data: missing `ssr_html`')
        return true
      }

      // Cached on the instance: `destroy` clears the host's children, so a
      // re-connect has to render from the already-parsed data.
      this.#data = data
    }

    this.#stopObserving()

    const namespace = makeEventNamespace()
    this.#namespace = namespace

    const layout = this.#data.options?.layout ?? 'pdp'
    const input = { host: this, data: this.#data, namespace }

    this.#controller =
      layout === 'quickview'
        ? createQuickviewController(input)
        : createPdpController(input)

    return true
  }

  connectedCallback() {
    this.#isMounted = true

    if (this.#tryInit()) {
      return
    }

    this.#observer = new MutationObserver(() => {
      this.#tryInit()
    })
    this.#observer.observe(this, { childList: true, subtree: true })

    queueMicrotask(() => {
      this.#tryInit()
    })
  }

  disconnectedCallback() {
    this.#isMounted = false
    this.#stopObserving()
    this.#controller?.destroy()
    this.#controller = null
    this.#namespace?.destroy()
    this.#namespace = null
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm)
}

export { ProductForm }
